/**
 * Public Ticket Registry - Dedicated Full-Page Arena Explorer
 * Elvis Aloha Collection
 * 
 * Mobile-first design with fixed shell layout:
 * - Fixed top toolbar
 * - Fixed bottom status bar
 * - Fixed 5-button mobile navigation
 * - Only center canvas pans/zooms
 * 
 * Desktop: Left sidebar + main workspace
 * 
 * FULLSCREEN ARCHITECTURE:
 * All interactive elements (sheets, modals, dropdowns) must be rendered
 * INSIDE the fullscreen root so they remain visible when fullscreen is active.
 * The fullscreen root wraps the entire registry application.
 */

import React, { useEffect, useRef, useState, useCallback, useMemo, createContext, useContext } from 'react';
import { Link, useSearchParams, useLocation, useNavigate, useParams } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { Helmet } from 'react-helmet-async';
import OpenSeadragon from 'openseadragon';
import { MapPin, Menu, Maximize2, Minimize2, Plus, Minus, RotateCcw, Home, Archive, Map, Ticket, BookOpen, ChevronLeft, ChevronRight, X, List, FileText } from 'lucide-react';
import { CHART_IDS, CHARTS } from '../../constants/mapEditor';
import TicketListView from '../../components/registry/TicketListView';
import ExactSeatOverlay from '../../components/ExactSeatOverlay';
import '../../styles/map-editor.css';
import '../../styles/ticket-list.css';

// Fullscreen context for portal targeting
const FullscreenContext = createContext(null);

// Mobile detection hook
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 900);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 900);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return isMobile;
};

// Fullscreen detection hook
const useFullscreen = (elementRef) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  useEffect(() => {
    const handleFullscreenChange = () => {
      const fullscreenElement = document.fullscreenElement || 
                                document.webkitFullscreenElement || 
                                document.mozFullScreenElement;
      setIsFullscreen(!!fullscreenElement);
    };
    
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
    };
  }, []);
  
  const toggleFullscreen = useCallback(() => {
    if (!elementRef.current) return;
    
    if (!document.fullscreenElement) {
      elementRef.current.requestFullscreen?.() || 
      elementRef.current.webkitRequestFullscreen?.() ||
      elementRef.current.mozRequestFullScreen?.();
    } else {
      document.exitFullscreen?.() ||
      document.webkitExitFullscreen?.() ||
      document.mozCancelFullScreen?.();
    }
  }, [elementRef]);
  
  return { isFullscreen, toggleFullscreen };
};

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

/**
 * RegistryPortal - Renders children into the fullscreen root when active
 * This ensures sheets, modals, and dropdowns remain visible in fullscreen mode
 */
const RegistryPortal = ({ children }) => {
  const portalRoot = useContext(FullscreenContext);
  
  if (!portalRoot) {
    // Fallback: render in place if no portal root available
    return <>{children}</>;
  }
  
  return createPortal(children, portalRoot);
};

/**
 * JumpToSectionSheet - Mobile bottom sheet for section navigation
 * Inline to ensure proper fullscreen portal integration
 */
const JumpToSectionSheet = ({ 
  isOpen, 
  onClose, 
  sections, 
  chartTargets, 
  selectedZone, 
  onJump 
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  
  // Build grouped options
  const groupedOptions = useMemo(() => {
    const groups = [
      { label: 'Quick Access', options: [
        ...(chartTargets?.['stage'] ? [{ id: 'stage', label: 'Stage Area' }] : []),
        ...(chartTargets?.['golden-circle'] ? [{ id: 'golden-circle', label: 'Golden Circle' }] : []),
        { id: 'full-arena', label: 'Full Arena' }
      ]},
      { label: 'Upper Sections', options: sections?.upper || [] },
      { label: 'Loge Sections', options: sections?.loge || [] },
      { label: 'Floor Sections', options: sections?.floor?.filter(s => !['golden-circle', 'stage'].includes(s.id)) || [] }
    ];
    return groups.filter(g => g.options.length > 0);
  }, [sections, chartTargets]);

  // Filter by search
  const filteredGroups = useMemo(() => {
    if (!searchQuery) return groupedOptions;
    const q = searchQuery.toLowerCase();
    return groupedOptions.map(group => ({
      ...group,
      options: group.options.filter(opt => opt.label.toLowerCase().includes(q))
    })).filter(group => group.options.length > 0);
  }, [groupedOptions, searchQuery]);

  const handleSelect = (zoneId) => {
    onJump(zoneId);
    onClose();
    setSearchQuery('');
  };

  if (!isOpen) return null;

  return (
    <RegistryPortal>
      <div className="jump-sheet-overlay" onClick={onClose} data-testid="jump-sheet-overlay">
        <div className="jump-sheet" onClick={e => e.stopPropagation()} data-testid="jump-sheet">
          {/* Header */}
          <div className="jump-sheet__header">
            <div className="jump-sheet__title">
              <MapPin size={20} />
              <span>Jump to Section</span>
            </div>
            <button 
              type="button" 
              className="jump-sheet__close" 
              onClick={onClose}
              aria-label="Close"
              data-testid="jump-sheet-close"
            >
              <X size={24} />
            </button>
          </div>

          {/* Search */}
          <div className="jump-sheet__search">
            <MapPin size={18} />
            <input
              type="text"
              placeholder="Search sections..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoFocus
              data-testid="jump-sheet-search"
            />
          </div>

          {/* Section List */}
          <div className="jump-sheet__content">
            {filteredGroups.length > 0 ? (
              filteredGroups.map(group => (
                <div key={group.label} className="jump-sheet__group">
                  <div className="jump-sheet__group-label">{group.label}</div>
                  <div className="jump-sheet__group-items">
                    {group.options.map(opt => (
                      <button
                        key={opt.id}
                        type="button"
                        className={`jump-sheet__item ${selectedZone === opt.id ? 'jump-sheet__item--active' : ''}`}
                        onClick={() => handleSelect(opt.id)}
                        data-testid={`jump-section-${opt.id}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))
            ) : (
              <div className="jump-sheet__empty">
                No sections match &ldquo;{searchQuery}&rdquo;
              </div>
            )}
          </div>
        </div>
      </div>
    </RegistryPortal>
  );
};

/**
 * MobileFiltersDrawer - Slide-out filters panel for mobile
 */
const MobileFiltersDrawer = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null;
  
  return (
    <RegistryPortal>
      <div className="arena-mobile-drawer-overlay" onClick={onClose} data-testid="filters-drawer-overlay">
        <div className="arena-mobile-drawer" onClick={e => e.stopPropagation()} data-testid="filters-drawer">
          <button 
            type="button"
            onClick={onClose}
            className="arena-mobile-drawer__close"
            data-testid="filters-drawer-close"
          >×</button>
          {children}
        </div>
      </div>
    </RegistryPortal>
  );
};

/**
 * KnownTicketsSheet - Mobile bottom sheet for viewing known tickets
 */
const KnownTicketsSheet = ({ isOpen, onClose, tickets, onSelectTicket, selectedTicket }) => {
  if (!isOpen) return null;
  
  return (
    <RegistryPortal>
      <div className="known-tickets-overlay" onClick={onClose} data-testid="known-tickets-overlay">
        <div className="known-tickets-sheet" onClick={e => e.stopPropagation()} data-testid="known-tickets-sheet">
          {/* Header */}
          <div className="known-tickets-sheet__header">
            <div className="known-tickets-sheet__title">
              <Ticket size={20} />
              <span>Known Tickets</span>
              <span className="known-tickets-sheet__count">{tickets?.length || 0}</span>
            </div>
            <button 
              type="button" 
              className="known-tickets-sheet__close" 
              onClick={onClose}
              aria-label="Close"
              data-testid="known-tickets-close"
            >
              <X size={24} />
            </button>
          </div>
          
          {/* Ticket List */}
          <div className="known-tickets-sheet__content">
            {tickets && tickets.length > 0 ? (
              tickets.map((ticket, idx) => (
                <div 
                  key={ticket.id || idx}
                  className={`known-ticket-item ${selectedTicket?.id === ticket.id ? 'known-ticket-item--selected' : ''}`}
                  onClick={() => onSelectTicket(ticket)}
                  data-testid={`known-ticket-${ticket.id || idx}`}
                >
                  <div className="known-ticket-item__thumb">
                    {ticket.thumbnail_url ? (
                      <img src={ticket.thumbnail_url} alt="Ticket" />
                    ) : (
                      <Ticket size={20} />
                    )}
                  </div>
                  <div className="known-ticket-item__info">
                    <div className="known-ticket-item__seat">
                      {ticket.section && `Sec ${ticket.section}`}
                      {ticket.row && ` · Row ${ticket.row}`}
                      {ticket.seat_number && ` · Seat ${ticket.seat_number}`}
                    </div>
                    <div className="known-ticket-item__type">{ticket.ticket_form || 'Full Ticket'}</div>
                  </div>
                </div>
              ))
            ) : (
              <div className="known-tickets-sheet__empty">
                <Ticket size={32} strokeWidth={1.5} />
                <p>No documented tickets match the current filters</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </RegistryPortal>
  );
};

// Mobile bottom sheet showing a single ticket's detail - opens when a public
// visitor taps an assigned seat on the map (mobile only; desktop keeps the
// existing side panel). Only ever receives the public-safe ticket shape.
const TicketDetailSheet = ({ isOpen, onClose, ticket }) => {
  if (!isOpen || !ticket) return null;

  return (
    <RegistryPortal>
      <div className="ticket-detail-sheet-overlay" onClick={onClose} data-testid="ticket-detail-sheet-overlay">
        <div
          className="ticket-detail-sheet"
          onClick={e => e.stopPropagation()}
          data-testid="ticket-detail-sheet"
          role="dialog"
          aria-modal="true"
          aria-label={`Ticket detail for ${ticket.registry_reference || 'selected seat'}`}
        >
          <div className="ticket-detail-sheet__handle" aria-hidden="true" />
          <div className="ticket-detail-sheet__header">
            <div className="ticket-detail-sheet__title">
              <Ticket size={20} />
              <span>{ticket.registry_reference || 'Documented Ticket'}</span>
            </div>
            <button
              type="button"
              className="ticket-detail-sheet__close"
              onClick={onClose}
              aria-label="Close ticket detail"
              data-testid="ticket-detail-sheet-close"
            >
              <X size={22} />
            </button>
          </div>

          <div className="ticket-detail-sheet__content">
            <div className="ticket-detail-sheet__seat-row">
              {ticket.section && <span className="ticket-detail-sheet__seat-pill">Section {ticket.section}</span>}
              {ticket.row && <span className="ticket-detail-sheet__seat-pill">Row {ticket.row}</span>}
              {(ticket.seat_number || ticket.seat) && <span className="ticket-detail-sheet__seat-pill">Seat {ticket.seat_number || ticket.seat}</span>}
            </div>

            <div className="ticket-detail-sheet__field">
              <span className="ticket-detail-sheet__label">Ticket Form</span>
              <span className="ticket-detail-sheet__value">{ticket.ticket_form || 'Full Ticket'}</span>
            </div>

            {ticket.public_collection_label && (
              <div className="ticket-detail-sheet__field">
                <span className="ticket-detail-sheet__label">Collection</span>
                <span className="ticket-detail-sheet__value">{ticket.public_collection_label}</span>
              </div>
            )}

            {ticket.grading_summary && (
              <div className="ticket-detail-sheet__field">
                <span className="ticket-detail-sheet__label">Grading</span>
                <span className="ticket-detail-sheet__value">{ticket.grading_summary}</span>
              </div>
            )}

            {ticket.verification_summary && (
              <div className="ticket-detail-sheet__field">
                <span className="ticket-detail-sheet__label">Verification</span>
                <span className="ticket-detail-sheet__value">{ticket.verification_summary}</span>
              </div>
            )}

            {ticket.historical_classifications?.length > 0 && (
              <div className="ticket-detail-sheet__tags">
                {ticket.historical_classifications.map(tag => (
                  <span key={tag} className="ticket-detail-sheet__tag">{tag}</span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </RegistryPortal>
  );
};

// Filter categories
const FILTER_CATEGORIES = {
  ticketType: {
    label: 'Ticket Type',
    options: [
      { id: 'full', label: 'Full Tickets' },
      { id: 'stub', label: 'Ticket Stubs' },
      { id: 'blank', label: 'Blank Tickets' },
      { id: 'unused', label: 'Unused Tickets' },
      { id: 'other', label: 'Other Ticket Forms' }
    ]
  },
  verification: {
    label: 'Verification & Grading',
    options: [
      { id: 'psa', label: 'PSA Graded' },
      { id: 'other-graded', label: 'Other Graded' },
      { id: 'visual-review', label: 'Visual Review Passed' },
      { id: 'expert', label: 'Expert Inspected' },
      { id: 'provenance', label: 'Provenance Documented' },
      { id: 'under-review', label: 'Under Review' }
    ]
  },
  collection: {
    label: 'Collection',
    options: [
      { id: 'principal', label: 'Principal Archive' },
      { id: 'private', label: 'Private Collections' },
      { id: 'institutional', label: 'Institutional Collections' },
      { id: 'external', label: 'Known External Tickets' },
      { id: 'withheld', label: 'Owner Identity Withheld' }
    ]
  },
  historical: {
    label: 'Historical Categories',
    options: [
      { id: 'elvis-50', label: 'Elvis Aloha 50' },
      { id: 'golden-circle', label: 'Golden Circle Tickets' },
      { id: 'graceland-custody', label: 'Graceland Custody' },
      { id: 'graceland-display', label: 'Graceland Display' },
      { id: 'stage-covered', label: 'Stage Covered' },
      { id: 'camera-area', label: 'Camera or Production Area' },
      { id: 'public-sale', label: 'Known Public Sale' }
    ]
  },
  status: {
    label: 'Ticket Status',
    options: [
      { id: 'confirmed', label: 'Confirmed Physical Ticket' },
      { id: 'photo-documented', label: 'Documented from Photographs' },
      { id: 'auction-documented', label: 'Documented from Auction Records' },
      { id: 'candidate', label: 'Candidate Under Review' },
      { id: 'no-surviving', label: 'No Known Surviving Ticket' }
    ]
  }
};

/**
 * CHART-SPECIFIC JUMP TARGETS
 * 
 * Jump targets are defined as native-image bounding boxes (x, y, width, height)
 * for each chart separately. This ensures accurate navigation regardless of 
 * which reference chart is active.
 * 
 * Coordinate System: Native image pixels (2550x3300 for all charts)
 * Status: 'owner_approved' = manually verified, 'internal' = provisional
 * 
 * The fitBounds approach with padding ensures the target is always visible
 * and appropriately zoomed.
 * 
 * COORDINATE REFERENCE (arena-stage-layout-1.png):
 * - Full image: 2550 x 3300 pixels
 * - Arena crop: x:70, y:200, w:2410, h:1900 (arenaCrop from mapEditor.js)
 * - Title "Arena - Stage Show" at y:0-200
 * - Arena bowl spans roughly x:350-2200, y:250-2100
 * - Stage positioned in lower-center of visible arena
 */
const JUMP_TARGETS = {
  // Concert Stage Reference (Default Aloha layout)
  'concert-stage-reference': {
    // Upper sections (outer bowl - numbered clockwise from upper-left)
    // These are at the TOP edge of the arena bowl
    'sec-14': { label: 'Section 14', nativeBounds: { x: 380, y: 480, width: 320, height: 400 }, status: 'owner_approved' },
    'sec-15': { label: 'Section 15', nativeBounds: { x: 560, y: 340, width: 320, height: 380 }, status: 'owner_approved' },
    'sec-16': { label: 'Section 16', nativeBounds: { x: 780, y: 240, width: 360, height: 380 }, status: 'owner_approved' },
    'sec-17': { label: 'Section 17', nativeBounds: { x: 1020, y: 200, width: 380, height: 360 }, status: 'owner_approved' },
    'sec-18': { label: 'Section 18', nativeBounds: { x: 1300, y: 200, width: 380, height: 360 }, status: 'owner_approved' },
    'sec-19': { label: 'Section 19', nativeBounds: { x: 1580, y: 240, width: 360, height: 380 }, status: 'owner_approved' },
    'sec-20': { label: 'Section 20', nativeBounds: { x: 1800, y: 340, width: 320, height: 400 }, status: 'owner_approved' },
    'sec-21': { label: 'Section 21', nativeBounds: { x: 1980, y: 480, width: 300, height: 420 }, status: 'owner_approved' },
    // Loge sections (middle tier)
    'loge-9': { label: 'Loge 9', nativeBounds: { x: 460, y: 760, width: 300, height: 360 }, status: 'owner_approved' },
    'loge-10': { label: 'Loge 10', nativeBounds: { x: 640, y: 580, width: 300, height: 340 }, status: 'owner_approved' },
    'loge-11': { label: 'Loge 11', nativeBounds: { x: 860, y: 460, width: 320, height: 320 }, status: 'owner_approved' },
    'loge-12': { label: 'Loge 12', nativeBounds: { x: 1100, y: 420, width: 340, height: 300 }, status: 'owner_approved' },
    'loge-13': { label: 'Loge 13', nativeBounds: { x: 1360, y: 460, width: 320, height: 320 }, status: 'owner_approved' },
    'loge-14': { label: 'Loge 14', nativeBounds: { x: 1580, y: 580, width: 300, height: 340 }, status: 'owner_approved' },
    'loge-15': { label: 'Loge 15', nativeBounds: { x: 1780, y: 760, width: 300, height: 360 }, status: 'owner_approved' },
    // Floor sections (Golden Circle and stage-adjacent)
    'floor-aa': { label: 'AA', nativeBounds: { x: 880, y: 1600, width: 350, height: 320 }, status: 'owner_approved' },
    'floor-bb': { label: 'BB', nativeBounds: { x: 1120, y: 1600, width: 350, height: 320 }, status: 'owner_approved' },
    'floor-cc': { label: 'CC', nativeBounds: { x: 1360, y: 1600, width: 350, height: 320 }, status: 'owner_approved' },
    // Floor sections near/under stage
    'floor-j': { label: 'J', nativeBounds: { x: 700, y: 1280, width: 320, height: 280 }, status: 'owner_approved' },
    'floor-k': { label: 'K', nativeBounds: { x: 960, y: 1280, width: 320, height: 280 }, status: 'owner_approved' },
    'floor-l': { label: 'L', nativeBounds: { x: 1220, y: 1280, width: 320, height: 280 }, status: 'owner_approved' },
    // Special zones
    'golden-circle': { label: 'Golden Circle', nativeBounds: { x: 800, y: 1520, width: 980, height: 460 }, status: 'owner_approved' },
    'stage': { label: 'Stage Area', nativeBounds: { x: 840, y: 1040, width: 860, height: 480 }, status: 'owner_approved' },
    'full-arena': { label: 'Full Arena', nativeBounds: { x: 70, y: 200, width: 2410, height: 1900 }, status: 'owner_approved' }
  },
  // 360 Stage Reference (same bowl sections, different floor config)
  '360-stage-reference': {
    'sec-14': { label: 'Section 14', nativeBounds: { x: 380, y: 480, width: 320, height: 400 }, status: 'owner_approved' },
    'sec-15': { label: 'Section 15', nativeBounds: { x: 560, y: 340, width: 320, height: 380 }, status: 'owner_approved' },
    'sec-16': { label: 'Section 16', nativeBounds: { x: 780, y: 240, width: 360, height: 380 }, status: 'owner_approved' },
    'sec-17': { label: 'Section 17', nativeBounds: { x: 1020, y: 200, width: 380, height: 360 }, status: 'owner_approved' },
    'sec-18': { label: 'Section 18', nativeBounds: { x: 1300, y: 200, width: 380, height: 360 }, status: 'owner_approved' },
    'sec-19': { label: 'Section 19', nativeBounds: { x: 1580, y: 240, width: 360, height: 380 }, status: 'owner_approved' },
    'sec-20': { label: 'Section 20', nativeBounds: { x: 1800, y: 340, width: 320, height: 400 }, status: 'owner_approved' },
    'sec-21': { label: 'Section 21', nativeBounds: { x: 1980, y: 480, width: 300, height: 420 }, status: 'owner_approved' },
    'loge-9': { label: 'Loge 9', nativeBounds: { x: 460, y: 760, width: 300, height: 360 }, status: 'owner_approved' },
    'loge-10': { label: 'Loge 10', nativeBounds: { x: 640, y: 580, width: 300, height: 340 }, status: 'owner_approved' },
    'loge-11': { label: 'Loge 11', nativeBounds: { x: 860, y: 460, width: 320, height: 320 }, status: 'owner_approved' },
    'loge-12': { label: 'Loge 12', nativeBounds: { x: 1100, y: 420, width: 340, height: 300 }, status: 'owner_approved' },
    'loge-13': { label: 'Loge 13', nativeBounds: { x: 1360, y: 460, width: 320, height: 320 }, status: 'owner_approved' },
    'loge-14': { label: 'Loge 14', nativeBounds: { x: 1580, y: 580, width: 300, height: 340 }, status: 'owner_approved' },
    'loge-15': { label: 'Loge 15', nativeBounds: { x: 1780, y: 760, width: 300, height: 360 }, status: 'owner_approved' },
    'floor-aa': { label: 'AA', nativeBounds: { x: 880, y: 1600, width: 350, height: 320 }, status: 'owner_approved' },
    'floor-bb': { label: 'BB', nativeBounds: { x: 1120, y: 1600, width: 350, height: 320 }, status: 'owner_approved' },
    'floor-cc': { label: 'CC', nativeBounds: { x: 1360, y: 1600, width: 350, height: 320 }, status: 'owner_approved' },
    'floor-j': { label: 'J', nativeBounds: { x: 700, y: 1280, width: 320, height: 280 }, status: 'owner_approved' },
    'floor-k': { label: 'K', nativeBounds: { x: 960, y: 1280, width: 320, height: 280 }, status: 'owner_approved' },
    'floor-l': { label: 'L', nativeBounds: { x: 1220, y: 1280, width: 320, height: 280 }, status: 'owner_approved' },
    'golden-circle': { label: 'Golden Circle', nativeBounds: { x: 800, y: 1520, width: 980, height: 460 }, status: 'owner_approved' },
    'stage': { label: 'Stage Area', nativeBounds: { x: 1000, y: 1200, width: 560, height: 400 }, status: 'owner_approved' },
    'full-arena': { label: 'Full Arena', nativeBounds: { x: 70, y: 200, width: 2410, height: 1900 }, status: 'owner_approved' }
  },
  // Boxing Reference
  'boxing-reference': {
    'sec-14': { label: 'Section 14', nativeBounds: { x: 380, y: 480, width: 320, height: 400 }, status: 'owner_approved' },
    'sec-15': { label: 'Section 15', nativeBounds: { x: 560, y: 340, width: 320, height: 380 }, status: 'owner_approved' },
    'sec-16': { label: 'Section 16', nativeBounds: { x: 780, y: 240, width: 360, height: 380 }, status: 'owner_approved' },
    'sec-17': { label: 'Section 17', nativeBounds: { x: 1020, y: 200, width: 380, height: 360 }, status: 'owner_approved' },
    'sec-18': { label: 'Section 18', nativeBounds: { x: 1300, y: 200, width: 380, height: 360 }, status: 'owner_approved' },
    'sec-19': { label: 'Section 19', nativeBounds: { x: 1580, y: 240, width: 360, height: 380 }, status: 'owner_approved' },
    'sec-20': { label: 'Section 20', nativeBounds: { x: 1800, y: 340, width: 320, height: 400 }, status: 'owner_approved' },
    'sec-21': { label: 'Section 21', nativeBounds: { x: 1980, y: 480, width: 300, height: 420 }, status: 'owner_approved' },
    'loge-9': { label: 'Loge 9', nativeBounds: { x: 460, y: 760, width: 300, height: 360 }, status: 'owner_approved' },
    'loge-10': { label: 'Loge 10', nativeBounds: { x: 640, y: 580, width: 300, height: 340 }, status: 'owner_approved' },
    'loge-11': { label: 'Loge 11', nativeBounds: { x: 860, y: 460, width: 320, height: 320 }, status: 'owner_approved' },
    'loge-12': { label: 'Loge 12', nativeBounds: { x: 1100, y: 420, width: 340, height: 300 }, status: 'owner_approved' },
    'loge-13': { label: 'Loge 13', nativeBounds: { x: 1360, y: 460, width: 320, height: 320 }, status: 'owner_approved' },
    'loge-14': { label: 'Loge 14', nativeBounds: { x: 1580, y: 580, width: 300, height: 340 }, status: 'owner_approved' },
    'loge-15': { label: 'Loge 15', nativeBounds: { x: 1780, y: 760, width: 300, height: 360 }, status: 'owner_approved' },
    'floor-aa': { label: 'AA', nativeBounds: { x: 880, y: 1600, width: 350, height: 320 }, status: 'owner_approved' },
    'floor-bb': { label: 'BB', nativeBounds: { x: 1120, y: 1600, width: 350, height: 320 }, status: 'owner_approved' },
    'floor-cc': { label: 'CC', nativeBounds: { x: 1360, y: 1600, width: 350, height: 320 }, status: 'owner_approved' },
    'floor-j': { label: 'J', nativeBounds: { x: 700, y: 1280, width: 320, height: 280 }, status: 'owner_approved' },
    'floor-k': { label: 'K', nativeBounds: { x: 960, y: 1280, width: 320, height: 280 }, status: 'owner_approved' },
    'floor-l': { label: 'L', nativeBounds: { x: 1220, y: 1280, width: 320, height: 280 }, status: 'owner_approved' },
    'golden-circle': { label: 'Golden Circle', nativeBounds: { x: 800, y: 1520, width: 980, height: 460 }, status: 'owner_approved' },
    'ring': { label: 'Boxing Ring', nativeBounds: { x: 1060, y: 1300, width: 440, height: 380 }, status: 'owner_approved' },
    'full-arena': { label: 'Full Arena', nativeBounds: { x: 70, y: 200, width: 2410, height: 1900 }, status: 'owner_approved' }
  }
};

// Build section lists for sidebar navigation from jump targets
const buildSectionListsFromTargets = (chartId) => {
  const targets = JUMP_TARGETS[chartId] || JUMP_TARGETS['concert-stage-reference'];
  
  const upper = [];
  const loge = [];
  const floor = [];
  
  Object.entries(targets).forEach(([id, target]) => {
    const item = { id, label: target.label, status: target.status };
    if (id.startsWith('sec-')) upper.push(item);
    else if (id.startsWith('loge-')) loge.push(item);
    else if (id.startsWith('floor-') || ['golden-circle', 'stage', 'ring'].includes(id)) floor.push(item);
  });
  
  // Sort by numeric portion of ID
  const sortByNum = (a, b) => {
    const numA = parseInt(a.id.replace(/\D/g, '')) || 0;
    const numB = parseInt(b.id.replace(/\D/g, '')) || 0;
    return numA - numB;
  };
  
  return {
    upper: upper.sort(sortByNum),
    loge: loge.sort(sortByNum),
    floor // Keep floor in defined order (AA, BB, CC, then J, K, L, etc.)
  };
};

/**
 * Validate that bounds fall within the image dimensions
 * Returns false if the target would result in a blank view
 */
const isBoundsInsideImage = (bounds, imageDimensions) => {
  if (!bounds || !imageDimensions) return false;
  const { x, y, width, height } = bounds;
  const { width: imgW, height: imgH } = imageDimensions;
  
  // Check bounds are positive and within image
  if (x < 0 || y < 0 || width <= 0 || height <= 0) return false;
  if (x + width > imgW + 100 || y + height > imgH + 100) return false; // Allow small margin
  
  return true;
};

// Filter Chip Component
const FilterChip = ({ label, onRemove }) => (
  <span style={{
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 8px',
    background: 'rgba(201, 169, 98, 0.2)',
    borderRadius: '12px',
    fontSize: '11px',
    color: '#C9A962'
  }}>
    {label}
    <button 
      onClick={onRemove}
      style={{ background: 'none', border: 'none', color: '#C9A962', cursor: 'pointer', padding: 0, fontSize: '14px' }}
    >×</button>
  </span>
);

// Collapsible Filter Section
const FilterSection = ({ title, options, activeFilters, onToggle, expanded, onToggleExpand }) => (
  <div style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
    <button 
      type="button"
      onClick={onToggleExpand}
      style={{
        width: '100%',
        padding: '10px 16px',
        background: 'transparent',
        border: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        cursor: 'pointer',
        color: '#888',
        fontSize: '10px',
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase'
      }}
    >
      {title}
      <span style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s', fontSize: '8px' }}>▼</span>
    </button>
    {expanded && (
      <div style={{ padding: '0 8px 8px' }}>
        {options.map(opt => {
          const isActive = activeFilters.has(opt.id);
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => onToggle(opt.id)}
              style={{
                width: '100%',
                padding: '6px 12px',
                marginBottom: '2px',
                background: isActive ? 'rgba(201, 169, 98, 0.15)' : 'transparent',
                border: 'none',
                borderRadius: '4px',
                color: isActive ? '#fff' : '#999',
                textAlign: 'left',
                cursor: 'pointer',
                fontSize: '12px',
                transition: 'all 0.15s'
              }}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    )}
  </div>
);

// Searchable Section Combobox Component
const SectionCombobox = ({ sections, onJump, selectedZone, chartTargets }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Build grouped options with zones
  const groupedOptions = useMemo(() => {
    const groups = [
      { 
        label: 'Upper Sections', 
        options: sections.upper || []
      },
      { 
        label: 'Loge Sections', 
        options: sections.loge || []
      },
      { 
        label: 'Floor Sections', 
        options: sections.floor?.filter(s => !['golden-circle', 'stage'].includes(s.id)) || []
      },
      { 
        label: 'Zones', 
        options: [
          ...(chartTargets?.['golden-circle'] ? [{ id: 'golden-circle', label: 'Golden Circle' }] : []),
          ...(chartTargets?.['stage'] ? [{ id: 'stage', label: 'Stage Area' }] : []),
          ...(chartTargets?.['ring'] ? [{ id: 'ring', label: 'Boxing Ring' }] : [])
        ]
      }
    ];
    return groups.filter(g => g.options.length > 0);
  }, [sections, chartTargets]);

  // Filter options by search query
  const filteredGroups = useMemo(() => {
    if (!searchQuery) return groupedOptions;
    const q = searchQuery.toLowerCase();
    return groupedOptions.map(group => ({
      ...group,
      options: group.options.filter(opt => opt.label.toLowerCase().includes(q))
    })).filter(group => group.options.length > 0);
  }, [groupedOptions, searchQuery]);

  // Get selected zone label
  const selectedLabel = useMemo(() => {
    if (!selectedZone) return null;
    for (const group of groupedOptions) {
      const found = group.options.find(o => o.id === selectedZone);
      if (found) return found.label;
    }
    return null;
  }, [selectedZone, groupedOptions]);

  // Handle selection
  const handleSelect = (zoneId) => {
    onJump(zoneId);
    setIsOpen(false);
    setSearchQuery('');
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Focus input when opening
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  return (
    <div style={{ padding: '12px', borderBottom: '1px solid rgba(255,255,255,0.08)' }} ref={dropdownRef}>
      <div style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.08em', color: '#888', marginBottom: '8px', textTransform: 'uppercase' }}>
        Jump to Section
      </div>
      
      {/* Combobox Trigger/Input */}
      <div style={{ position: 'relative' }}>
        <div
          onClick={() => setIsOpen(!isOpen)}
          style={{
            width: '100%',
            padding: '10px 32px 10px 12px',
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.15)',
            borderRadius: '6px',
            color: selectedLabel ? '#fff' : '#888',
            fontSize: '13px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
          data-testid="section-combobox-trigger"
        >
          {isOpen ? (
            <input
              ref={inputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              placeholder="Type to search..."
              style={{
                width: '100%',
                background: 'transparent',
                border: 'none',
                color: '#fff',
                fontSize: '13px',
                outline: 'none'
              }}
              data-testid="section-search-input"
            />
          ) : (
            <span>{selectedLabel || 'Select a section or zone'}</span>
          )}
          <span style={{ 
            position: 'absolute', 
            right: '12px', 
            top: '50%', 
            transform: `translateY(-50%) rotate(${isOpen ? '180deg' : '0'})`,
            transition: 'transform 0.2s',
            fontSize: '10px',
            color: '#888'
          }}>▼</span>
        </div>

        {/* Dropdown */}
        {isOpen && (
          <div style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: '4px',
            background: '#252525',
            border: '1px solid rgba(255,255,255,0.15)',
            borderRadius: '6px',
            maxHeight: '280px',
            overflowY: 'auto',
            zIndex: 1000,
            boxShadow: '0 8px 24px rgba(0,0,0,0.4)'
          }}>
            {filteredGroups.length > 0 ? (
              filteredGroups.map((group, idx) => (
                <div key={group.label}>
                  {idx > 0 && <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }} />}
                  <div style={{ 
                    padding: '8px 12px 4px', 
                    fontSize: '9px', 
                    fontWeight: 700, 
                    color: '#666', 
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                    position: 'sticky',
                    top: 0,
                    background: '#252525'
                  }}>
                    {group.label}
                  </div>
                  {group.options.map(opt => (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => handleSelect(opt.id)}
                      data-testid={`jump-${opt.id}`}
                      style={{
                        display: 'block',
                        width: '100%',
                        padding: '8px 12px',
                        background: selectedZone === opt.id ? 'rgba(201, 169, 98, 0.2)' : 'transparent',
                        border: 'none',
                        color: selectedZone === opt.id ? '#C9A962' : '#ccc',
                        textAlign: 'left',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: selectedZone === opt.id ? 600 : 400,
                        transition: 'background 0.15s'
                      }}
                      onMouseEnter={(e) => e.target.style.background = 'rgba(255,255,255,0.05)'}
                      onMouseLeave={(e) => e.target.style.background = selectedZone === opt.id ? 'rgba(201, 169, 98, 0.2)' : 'transparent'}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              ))
            ) : (
              <div style={{ padding: '16px', textAlign: 'center', color: '#666', fontSize: '12px' }}>
                No sections match "{searchQuery}"
              </div>
            )}
          </div>
        )}
      </div>

      {/* Selected Section Display */}
      {selectedLabel && (
        <div style={{ 
          marginTop: '8px', 
          padding: '6px 10px', 
          background: 'rgba(201, 169, 98, 0.15)', 
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <span style={{ fontSize: '12px', color: '#C9A962', fontWeight: 500 }}>
            📍 {selectedLabel}
          </span>
          <button
            type="button"
            onClick={() => onJump('full-arena')}
            style={{
              background: 'none',
              border: 'none',
              color: '#888',
              fontSize: '10px',
              cursor: 'pointer',
              padding: '2px 6px'
            }}
            title="View full arena"
          >
            Reset
          </button>
        </div>
      )}
    </div>
  );
};

// Ticket Card Component - Compact bento-style card for results list
const TicketCard = ({ ticket, isSelected, onSelect, onZoom }) => {
  const seatInfo = { section: ticket?.section || '--', row: ticket?.row || '--', seat: ticket?.seat_number || ticket?.seat || '--' };
  const ticketType = ticket?.ticket_form || 'Ticket';
  const grading = ticket?.grading_summary || null;
  const collection = ticket?.public_collection_label || null;
  const historicalTags = ticket?.historical_classifications || [];
  
  return (
    <div 
      className={`ticket-card ${isSelected ? 'ticket-card--selected' : ''}`}
      onClick={() => onSelect(ticket)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onSelect(ticket)}
    >
      {/* Thumbnail */}
      <div className="ticket-card__thumb">
        {ticket?.image_url ? (
          <img src={ticket.image_url} alt="Ticket" />
        ) : (
          <div className="ticket-card__thumb-placeholder">
            <Ticket size={24} />
          </div>
        )}
      </div>
      
      {/* Info */}
      <div className="ticket-card__info">
        <div className="ticket-card__seat">
          <span className="ticket-card__seat-label">Sec</span>
          <span className="ticket-card__seat-value">{seatInfo.section}</span>
          <span className="ticket-card__seat-sep">·</span>
          <span className="ticket-card__seat-label">Row</span>
          <span className="ticket-card__seat-value">{seatInfo.row}</span>
          <span className="ticket-card__seat-sep">·</span>
          <span className="ticket-card__seat-label">Seat</span>
          <span className="ticket-card__seat-value">{seatInfo.seat}</span>
        </div>
        <div className="ticket-card__type">
          {ticketType}
          {grading && <span className="ticket-card__grading"> · {grading}</span>}
        </div>
        {collection && <div className="ticket-card__collection">{collection}</div>}
        {historicalTags.length > 0 && (
          <div className="ticket-card__tags">
            {historicalTags.slice(0, 2).map(tag => (
              <span key={tag} className="ticket-card__tag">{tag}</span>
            ))}
          </div>
        )}
      </div>
      
      {/* Zoom button */}
      <button 
        type="button"
        className="ticket-card__zoom"
        onClick={(e) => { e.stopPropagation(); onZoom(ticket); }}
        title="Zoom to seat"
      >
        <MapPin size={16} />
      </button>
    </div>
  );
};

// Right Panel - Ticket Results or Detail View with persistent toggle
const TicketResultsPanel = ({ 
  tickets, 
  selectedTicket, 
  onSelectTicket, 
  onZoomToTicket,
  isOpen,
  onToggle,
  activeFilters,
  filterLabels,
  onClearFilters
}) => {
  const filteredTickets = tickets || [];
  const ticketCount = filteredTickets.length;
  
  return (
    <div 
      className={`ticket-results-shell ${isOpen ? '' : 'is-collapsed'}`}
      style={{
        // Expanded: full panel width; Collapsed: narrow rail
        width: isOpen ? '320px' : '40px',
        transition: 'width 0.2s ease',
        flexShrink: 0,
        position: 'relative',
        background: isOpen ? '#1a1a1a' : 'transparent',
        borderLeft: isOpen ? '1px solid rgba(255,255,255,0.1)' : 'none'
      }}
    >
      {/* Collapsed state - entire rail is clickable */}
      {!isOpen && (
        <button 
          type="button"
          className="known-tickets-collapsed-rail"
          onClick={() => onToggle(true)}
          aria-expanded="false"
          aria-controls="known-ticket-results"
          aria-label="Open Known Tickets panel"
          data-testid="results-panel-collapsed-rail"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(26, 26, 26, 0.95)',
            border: 'none',
            borderLeft: '1px solid rgba(255,255,255,0.1)',
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            padding: '16px 0'
          }}
        >
          <ChevronLeft size={18} style={{ color: '#888' }} />
          <span style={{ 
            writingMode: 'vertical-rl', 
            textOrientation: 'mixed',
            color: '#888',
            fontSize: '11px',
            fontWeight: 500,
            letterSpacing: '0.5px'
          }}>
            {ticketCount} Known
          </span>
        </button>
      )}
      
      {/* Expanded state - panel with clickable header */}
      {isOpen && (
        <aside id="known-ticket-results" className="ticket-results-panel" style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%'
        }}>
          {/* Panel Header - entire header is clickable to collapse */}
          <button 
            type="button"
            className="known-tickets-header"
            onClick={() => onToggle(false)}
            aria-expanded="true"
            aria-controls="known-ticket-results"
            data-testid="results-panel-header"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              padding: '12px 16px',
              background: 'transparent',
              border: 'none',
              borderBottom: '1px solid rgba(255,255,255,0.1)',
              cursor: 'pointer',
              textAlign: 'left'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ color: '#C9A962', fontWeight: 600, fontSize: '14px' }}>{ticketCount}</span>
              <span style={{ color: '#fff', fontSize: '13px', fontWeight: 500 }}>Known Tickets</span>
            </div>
            <ChevronRight size={18} style={{ color: '#888' }} />
          </button>
          
          {/* Active Filters */}
          {filterLabels && filterLabels.length > 0 && (
            <div style={{ 
              padding: '8px 12px', 
              borderBottom: '1px solid rgba(255,255,255,0.05)',
              display: 'flex',
              flexWrap: 'wrap',
              gap: '4px'
            }}>
              {filterLabels.map(f => (
                <FilterChip key={f.id} label={f.label} onRemove={() => {}} />
              ))}
            </div>
          )}
          
          {/* Results List */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '8px' }}>
            {ticketCount > 0 ? (
              filteredTickets.map((ticket, idx) => (
                <TicketCard 
                  key={ticket.id || idx}
                  ticket={ticket}
                  isSelected={selectedTicket?.id === ticket.id}
                  onSelect={onSelectTicket}
                  onZoom={onZoomToTicket}
                />
              ))
            ) : (
              <div style={{ 
                padding: '32px 16px', 
                textAlign: 'center',
                color: '#666'
              }}>
                <Ticket size={32} strokeWidth={1.5} style={{ marginBottom: '12px', opacity: 0.5 }} />
                <p style={{ fontSize: '12px', margin: 0 }}>
                  {activeFilters?.size > 0 
                    ? 'No tickets match the current filters'
                    : `${ticketCount} documented tickets (0 mapped to geometry)`
                  }
                </p>
                {activeFilters?.size > 0 && onClearFilters && (
                  <button 
                    type="button" 
                    onClick={onClearFilters}
                    style={{
                      marginTop: '12px',
                      background: 'none',
                      border: '1px solid rgba(201, 169, 98, 0.3)',
                      borderRadius: '4px',
                      padding: '6px 12px',
                      color: '#C9A962',
                      fontSize: '11px',
                      cursor: 'pointer'
                    }}
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            )}
          </div>
        </aside>
      )}
    </div>
  );
};

// Main Registry Sidebar
const RegistrySidebar = ({ 
  activeFilters, 
  toggleFilter, 
  clearFilters,
  onJumpToSection,
  ticketCount,
  mappedCount = 0,
  activeFilterCount = 0,
  isMobile,
  onClose,
  sections,
  selectedZone,
  jumpError,
  activeChartId
}) => {
  // Only one accordion open at a time
  const [expandedSection, setExpandedSection] = useState(null);

  const toggleExpand = (id) => {
    setExpandedSection(prev => prev === id ? null : id);
  };

  const handleAction = (action) => {
    action();
    if (isMobile && onClose) onClose();
  };

  const activeFilterLabels = useMemo(() => {
    const labels = [];
    Object.values(FILTER_CATEGORIES).forEach(cat => {
      cat.options.forEach(opt => {
        if (activeFilters.has(opt.id)) labels.push({ id: opt.id, label: opt.label });
      });
    });
    return labels;
  }, [activeFilters]);

  // Get chart-specific jump targets
  const chartTargets = JUMP_TARGETS[activeChartId] || JUMP_TARGETS['concert-stage-reference'];

  return (
    <aside 
      className="ticket-registry-sidebar"
      style={{
        width: '280px',
        background: '#171717',
        minHeight: '100vh',
        flexShrink: 0,
        position: 'relative',
        // Only use z-index in desktop mode, not in drawer
        zIndex: isMobile ? 'auto' : 50,
        pointerEvents: 'auto',
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto'
      }}
    >
      {/* Header */}
      <div style={{ padding: '16px', borderBottom: '1px solid rgba(255,255,255,0.1)', textAlign: 'center' }}>
        <Link to="/" style={{ display: 'block', marginBottom: '8px' }}>
          <img 
            src="/assets/branding/logo-sidebar-white.png" 
            alt="Elvis Aloha Collection"
            style={{ 
              width: '100px', height: '100px', objectFit: 'cover', objectPosition: 'center 42%',
              borderRadius: '50%', display: 'block', margin: '0 auto',
              border: '2px solid #C9A962', boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
            }}
          />
        </Link>
        <div style={{ fontSize: '9px', color: '#C9A962', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', margin: 0 }} role="heading" aria-level="1">
          Arena & Ticket Registry
        </div>
      </div>

      {/* Results Count */}
      <div style={{ padding: '10px 16px', background: 'rgba(201, 169, 98, 0.1)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <div style={{ fontSize: '13px', color: '#C9A962', fontWeight: 600 }} data-testid="arena-ticket-count">
          {ticketCount} Documented Tickets
        </div>
        <div style={{ fontSize: '10px', color: '#888' }}>
          {activeFilterLabels.length > 0 ? 'Matching current filters' : `${mappedCount} mapped on this chart`}
        </div>
      </div>

      {/* Clear Filters Control - Only visible when filters are active */}
      {activeFilterCount > 0 && (
        <div style={{ 
          padding: '10px 16px', 
          background: 'rgba(201, 169, 98, 0.08)',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span style={{ fontSize: '11px', color: '#888' }}>
            Filters · <span style={{ color: '#C9A962' }}>{activeFilterCount} active</span>
          </span>
          <button 
            type="button" 
            onClick={() => handleAction(clearFilters)}
            className="registry-clear-filters"
            aria-label={`Clear all ${activeFilterCount} active filters`}
            style={{
              background: 'none',
              border: '1px solid rgba(201, 169, 98, 0.3)',
              borderRadius: '4px',
              padding: '4px 10px',
              color: '#C9A962',
              fontSize: '10px',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 0.15s ease'
            }}
          >
            Clear all
          </button>
        </div>
      )}

      {/* Jump Error Notice */}
      {jumpError && (
        <div style={{ 
          padding: '10px 16px', 
          background: 'rgba(220, 53, 69, 0.15)', 
          borderBottom: '1px solid rgba(220, 53, 69, 0.3)',
          fontSize: '11px',
          color: '#ff6b6b'
        }}>
          {jumpError}
        </div>
      )}

      {/* Section Jump Combobox */}
      <SectionCombobox 
        sections={sections} 
        onJump={(zoneId) => handleAction(() => onJumpToSection(zoneId))}
        selectedZone={selectedZone}
        chartTargets={chartTargets}
      />

      {/* Filter Categories - Exclusive Accordions */}
      <div style={{ flex: 1 }}>
        {Object.entries(FILTER_CATEGORIES).map(([key, cat]) => (
          <FilterSection
            key={key}
            title={cat.label}
            options={cat.options}
            activeFilters={activeFilters}
            onToggle={(id) => handleAction(() => toggleFilter(id))}
            expanded={expandedSection === key}
            onToggleExpand={() => toggleExpand(key)}
          />
        ))}
      </div>

      {/* Active Filters */}
      {activeFilterLabels.length > 0 && (
        <div style={{ padding: '12px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <span style={{ fontSize: '10px', color: '#888', fontWeight: 600 }}>ACTIVE FILTERS</span>
            <button type="button" onClick={() => handleAction(clearFilters)} style={{
              background: 'none', border: 'none', color: '#C9A962', fontSize: '10px', cursor: 'pointer'
            }}>Clear All</button>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
            {activeFilterLabels.map(f => (
              <FilterChip key={f.id} label={f.label} onRemove={() => handleAction(() => toggleFilter(f.id))} />
            ))}
          </div>
        </div>
      )}

      {/* Return Link */}
      <div style={{ padding: '16px', borderTop: '1px solid rgba(255,255,255,0.1)', marginTop: 'auto' }}>
        <Link to="/" style={{
          display: 'flex', alignItems: 'center', gap: '8px',
          color: '#888', textDecoration: 'none', fontSize: '13px'
        }}>
          ← Return to Main Site
        </Link>
      </div>
    </aside>
  );
};

// P0 DIAGNOSTIC: Arena lifecycle instrumentation - OPT-IN ONLY, disabled by default.
// Enable in Preview/dev by running: localStorage.setItem('arena_debug', '1') then reload.
// Never exposed publicly by default; purely additive console logging, no behavior change.
const isArenaDebugEnabled = () => {
  try {
    return typeof window !== 'undefined' && window.localStorage?.getItem('arena_debug') === '1';
  } catch (e) {
    return false;
  }
};
const arenaLog = (event, data) => {
  if (!isArenaDebugEnabled()) return;
  // eslint-disable-next-line no-console
  console.log(`[ArenaDiag] ${event}`, { t: Date.now(), ...data });
};

// Main Component
const TicketRegistryPage = ({ view = 'map' }) => {
  // Refs
  const containerRef = useRef(null);
  const viewerRef = useRef(null);
  const fullscreenRootRef = useRef(null); // Root element for fullscreen
  const loadedChartUrlRef = useRef(null); // Track which chart URL is currently loaded
  const constraintBoundsRef = useRef(null); // Current constraint bounds (mobileCrop, arenaCrop, or fullChart)
  const constraintModeRef = useRef('arena'); // 'arena' or 'fullChart'
  const currentChartRef = useRef(null); // Track current chart for constraint calculations
  const isMountedRef = useRef(true); // P0 FIX: Track component mount state for async cleanup
  const lastSelectedSeatElRef = useRef(null); // Seat overlay id to restore focus to after closing the ticket panel/sheet
  const mountCountRef = useRef(0); // P0 DIAGNOSTIC: how many times this component instance has mounted
  const osdInitRunCountRef = useRef(0); // P0 DIAGNOSTIC: how many times the OSD init effect has run
  const [mapLoadError, setMapLoadError] = useState(null); // P0 RECOVERY: map init failure state
  const [mapRetryToken, setMapRetryToken] = useState(0); // P0 RECOVERY: bump to force a retry
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const isMobile = useIsMobile();
  
  // Current view mode - use internal state to prevent remounting during view switches
  // Initialize from prop but manage internally
  const [currentView, setCurrentView] = useState(view);
  
  // Sync with route changes (when user navigates directly via URL)
  useEffect(() => {
    setCurrentView(view);
  }, [view]);
  
  // Fullscreen state
  const { isFullscreen, toggleFullscreen } = useFullscreen(fullscreenRootRef);
  
  // Chart persistence: URL param → localStorage → default
  const getInitialChart = () => {
    const urlChart = searchParams.get('chart');
    if (urlChart && CHARTS[urlChart]) return urlChart;
    
    const storedChart = localStorage.getItem('arena-chart');
    if (storedChart && CHARTS[storedChart]) return storedChart;
    
    return CHART_IDS.CONCERT_STAGE;
  };
  
  // Zone persistence: URL param only (validated against current chart)
  const getInitialZone = (chartId) => {
    const urlZone = searchParams.get('zone');
    if (urlZone && JUMP_TARGETS[chartId]?.[urlZone]) return urlZone;
    return null;
  };
  
  // State
  const [activeChartId, setActiveChartIdState] = useState(getInitialChart);
  const [selectedZone, setSelectedZone] = useState(() => getInitialZone(getInitialChart()));
  const [jumpError, setJumpError] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const [currentZoom, setCurrentZoom] = useState(1);
  const [publishedOverlays, setPublishedOverlays] = useState([]);
  const [activeFilters, setActiveFilters] = useState(new Set());
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [jumpSheetOpen, setJumpSheetOpen] = useState(false);
  const [knownTicketsOpen, setKnownTicketsOpen] = useState(false); // Known tickets sheet (mobile)
  // Right panel: collapsed by default, persist manual preference in sessionStorage
  const [rightPanelOpen, setRightPanelOpen] = useState(() => {
    const saved = sessionStorage.getItem('registry-panel-open');
    return saved === 'true' ? true : false; // Default to collapsed
  });
  const [selectedTicket, setSelectedTicket] = useState(null); // Currently selected ticket for detail view
  const [portalLayerElement, setPortalLayerElement] = useState(null); // Portal target element
  const [allTickets, setAllTickets] = useState([]); // All tickets for list view
  const [ticketsLoading, setTicketsLoading] = useState(false);
  // Track if user manually toggled the panel (vs auto-expand from ticket selection)
  const userManualPanelToggleRef = useRef(false);
  // Transform state for SVG overlay rendering
  const [mapTransform, setMapTransform] = useState({ scale: 1, translateX: 0, translateY: 0 });
  
  const navigate = useNavigate();
  const params = useParams(); // For ticket detail view
  
  // P0 FIX: Track component mount state for safe async cleanup
  useEffect(() => {
    isMountedRef.current = true;
    mountCountRef.current += 1;
    arenaLog('component-mount', { mountCount: mountCountRef.current, view, currentView, route: location.pathname });
    return () => {
      isMountedRef.current = false;
      arenaLog('component-unmount', { mountCount: mountCountRef.current, route: location.pathname });
    };
  }, []);
  
  // Callback ref to capture portal layer element
  const portalLayerRefCallback = useCallback((node) => {
    if (node !== null) {
      setPortalLayerElement(node);
    }
  }, []);
  
  const activeChart = useMemo(() => CHARTS[activeChartId], [activeChartId]);
  const chartTargets = useMemo(() => JUMP_TARGETS[activeChartId] || JUMP_TARGETS['concert-stage-reference'], [activeChartId]);
  
  // Build chart-specific section lists
  const sectionLists = useMemo(() => buildSectionListsFromTargets(activeChartId), [activeChartId]);
  
  // Fetch all public tickets on mount - shared by both Map and List views
  useEffect(() => {
    const fetchTickets = async () => {
      setTicketsLoading(true);
      try {
        const response = await fetch(`${API_BASE}/api/seat-map/public/tickets?per_page=500`);
        if (response.ok) {
          const data = await response.json();
          setAllTickets(data.tickets || []);
        }
      } catch (err) {
        console.error('Failed to fetch tickets:', err);
      } finally {
        setTicketsLoading(false);
      }
    };
    fetchTickets();
  }, []);

  // Fetch published, canonically-assigned seats for current chart - drives the "mapped" count.
  // CANONICAL SOURCE ONLY: seat_geometries + ticket_assignments via the existing public-safe
  // /seat-geometries endpoint (never the deprecated placed_seats collection). For an
  // unauthenticated caller this endpoint already filters to published + owner_approved
  // geometry only, and returns a clean 200 {seats: []} for an empty chart - no warning.
  // NOTE: a third-party analytics script (PostHog session-recorder, loaded by the
  // platform's emergent-main.js) monkey-patches window.fetch and can, in a short window
  // during initial page load, throw a spurious TypeError even though the real request
  // reaches the server successfully - the retry loop below absorbs that race.
  useEffect(() => {
    if (!activeChartId) return;
    
    const controller = new AbortController();
    
    const fetchSeatsOnce = () =>
      fetch(`${API_BASE}/api/seat-map/seat-geometries/${activeChartId}?include_assignments=true`, {
        signal: controller.signal
      });
    
    const fetchMappedTickets = async () => {
      let seatsRes;
      let lastErr = null;
      // Up to 3 attempts total, with increasing delay - absorbs the
      // third-party analytics fetch-instrumentation race (see comment above),
      // which is a short, one-time window during initial page load.
      for (const delayMs of [0, 500, 900]) {
        if (delayMs > 0) await new Promise(r => setTimeout(r, delayMs));
        try {
          seatsRes = await fetchSeatsOnce();
          lastErr = null;
          break;
        } catch (err) {
          if (err.name === 'AbortError') return;
          lastErr = err;
        }
      }
      
      if (lastErr) {
        console.error('Failed to fetch mapped tickets:', lastErr);
        setPublishedOverlays([]);
        return;
      }
      
      if (!seatsRes.ok) {
        setPublishedOverlays([]);
        return;
      }
      
      const seatsData = await seatsRes.json();
      const assignedSeats = (seatsData.seats || []).filter(s => s.is_assigned);
      
      // Match canonically-assigned seats to their ticket record via the assignment's
      // public ticket reference (never section/row/seat guessing or legacy seat ids).
      const matchedTickets = [];
      assignedSeats.forEach(seat => {
        const ticket = allTickets.find(t =>
          t.registry_reference === seat.ticket_reference || t.public_reference === seat.ticket_reference
        );
        if (ticket) {
          matchedTickets.push({
            ...ticket,
            geometry_id: seat.id,
            geometry_x: seat.native_x,
            geometry_y: seat.native_y,
            seat_template_id: seat.template_id
          });
        }
      });
      
      setPublishedOverlays(matchedTickets);
    };
    
    // Only fetch once we have tickets loaded
    if (allTickets.length > 0) {
      fetchMappedTickets();
    }
    
    return () => controller.abort();
  }, [activeChartId, allTickets]);

  // Wrapped setActiveChartId that also persists to localStorage and validates zone
  const setActiveChartId = useCallback((newChartId) => {
    if (!CHARTS[newChartId]) return;
    
    // Persist to localStorage
    localStorage.setItem('arena-chart', newChartId);
    
    // Update state
    setActiveChartIdState(newChartId);
    
    // Validate current zone against new chart
    setSelectedZone(prev => {
      if (prev && !JUMP_TARGETS[newChartId]?.[prev]) {
        // Zone not available on new chart - clear it
        return null;
      }
      return prev;
    });
  }, []);

  // Filter toggle
  const toggleFilter = useCallback((filterId) => {
    setActiveFilters(prev => {
      const next = new Set(prev);
      if (next.has(filterId)) next.delete(filterId);
      else next.add(filterId);
      return next;
    });
  }, []);

  const clearFilters = useCallback(() => {
    setActiveFilters(new Set());
    setSelectedZone(null); // Also clear selected zone
    // Update URL to remove filter-related params
    const newParams = { chart: activeChartId };
    setSearchParams(newParams);
  }, [activeChartId, setSearchParams]);

  // Panel toggle with persistence
  const toggleRightPanel = useCallback((forceState) => {
    setRightPanelOpen(prev => {
      const next = forceState !== undefined ? forceState : !prev;
      sessionStorage.setItem('registry-panel-open', String(next));
      userManualPanelToggleRef.current = true;
      return next;
    });
  }, []);

  // Auto-expand panel when ticket is selected
  const handleSelectTicket = useCallback((ticket) => {
    setSelectedTicket(ticket);
    if (ticket) {
      // Expand panel when ticket is selected (unless user manually collapsed it)
      setRightPanelOpen(true);
    }
  }, []);

  // Get active filter count for display
  const activeFilterCount = activeFilters.size;

  // Close the ticket detail (desktop side panel or mobile bottom sheet) and
  // return focus to the seat that opened it - never destroys map state.
  const closeTicketDetail = useCallback(() => {
    setSelectedTicket(null);
    if (!isMobile) setRightPanelOpen(false);
    const seatId = lastSelectedSeatElRef.current;
    if (seatId) {
      requestAnimationFrame(() => {
        document.querySelector(`[data-seat-id="${seatId}"] .exact-seat-hit`)?.focus();
      });
    }
  }, [isMobile]);

  // Escape closes the ticket detail. Browser Back closes it first (pushes a
  // history entry when opened so the back gesture doesn't leave the page).
  useEffect(() => {
    if (!selectedTicket) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeTicketDetail();
    };
    window.addEventListener('keydown', onKeyDown);
    window.history.pushState({ ticketDetailOpen: true }, '');
    const onPopState = () => closeTicketDetail();
    window.addEventListener('popstate', onPopState);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('popstate', onPopState);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!selectedTicket]);

  /**
   * Jump to section using fitBounds with native image coordinates
   * 
   * This function:
   * 1. Looks up the zone's bounding box for the active chart
   * 2. Validates the bounds fall within the image
   * 3. Converts native image rect to viewport rect using correct API signature
   * 4. Validates the viewport rect is valid
   * 5. Applies padding and calls fitBounds
   * 6. Updates URL and selected state
   */
  const jumpToSection = useCallback((zoneId) => {
    setJumpError(null);
    
    if (!viewerRef.current) {
      setJumpError('Map viewer not ready. Please wait.');
      return;
    }
    
    const tiledImage = viewerRef.current.world.getItemAt(0);
    if (!tiledImage) {
      setJumpError('Map image not loaded. Please wait.');
      return;
    }
    
    // Ensure tiled image is fully loaded
    if (viewerRef.current.world.getItemCount() === 0) {
      setJumpError('Map image not fully loaded. Please wait.');
      return;
    }
    
    // Get chart-specific jump targets
    const chartTargets = JUMP_TARGETS[activeChartId];
    if (!chartTargets) {
      setJumpError('No jump targets defined for this chart layout.');
      return;
    }
    
    // Get the specific zone's bounding box
    const target = chartTargets[zoneId];
    if (!target) {
      setJumpError(`This section has not yet been mapped for the selected reference layout.`);
      return;
    }
    
    const nativeBounds = target.nativeBounds;
    const imageDimensions = { width: activeChart.width, height: activeChart.height };
    
    // Validate bounds are inside the image
    if (!isBoundsInsideImage(nativeBounds, imageDimensions)) {
      setJumpError('This section has invalid map coordinates.');
      return;
    }
    
    // Convert native image rect to OpenSeadragon viewport rect
    // Use the correct API signature: imageToViewportRectangle(x, y, width, height)
    const viewportRect = tiledImage.imageToViewportRectangle(
      nativeBounds.x,
      nativeBounds.y,
      nativeBounds.width,
      nativeBounds.height
    );
    
    // Validate the viewport rect is valid
    const isValidRect = 
      Number.isFinite(viewportRect.x) &&
      Number.isFinite(viewportRect.y) &&
      Number.isFinite(viewportRect.width) &&
      Number.isFinite(viewportRect.height) &&
      viewportRect.width > 0 &&
      viewportRect.height > 0;
    
    if (!isValidRect) {
      // Recovery: return to fit arena instead of blank map
      setJumpError('This section has not yet been mapped.');
      const crop = activeChart.arenaCrop;
      if (crop) {
        const arenaBounds = tiledImage.imageToViewportRectangle(crop.x, crop.y, crop.width, crop.height);
        viewerRef.current.viewport.fitBounds(arenaBounds, false);
      }
      return;
    }
    
    // Apply padding (inflate by 20% on each side for visual context)
    const paddingFactor = 0.20;
    const paddedBounds = new OpenSeadragon.Rect(
      viewportRect.x - viewportRect.width * paddingFactor,
      viewportRect.y - viewportRect.height * paddingFactor,
      viewportRect.width * (1 + paddingFactor * 2),
      viewportRect.height * (1 + paddingFactor * 2)
    );
    
    // Navigate to the target with animation
    viewerRef.current.viewport.fitBounds(paddedBounds, true);
    
    // Apply constraints to ensure we don't go out of bounds
    viewerRef.current.viewport.applyConstraints();
    
    // Update selected zone state
    setSelectedZone(zoneId);
    
    // Update URL with chart and zone
    setSearchParams({ chart: activeChartId, zone: zoneId });
  }, [activeChartId, activeChart, setSearchParams]);

  // Track if user has manually interacted with the map
  const userHasInteracted = useRef(false);

  /**
   * P0 FIX: Safe viewer check - returns true only if viewer is fully initialized and has valid drawer
   * Prevents "options.drawer is required" errors from stale async callbacks
   */
  const isViewerValid = useCallback((viewer) => {
    try {
      return viewer && 
             viewer.isOpen && 
             viewer.isOpen() && 
             viewer.world && 
             viewer.world.getItemCount() > 0 &&
             viewer.drawer; // Critical: drawer must exist
    } catch (e) {
      return false;
    }
  }, []);

  /**
   * Crop-Aware Constraint Helper
   * Prevents users from panning the chart too far into blank space.
   * Ensures the viewport doesn't show too much content outside the constraint bounds.
   */
  const applyCropAwareConstraints = useCallback((viewer, chart, forMobile = false) => {
    // P0 FIX: Verify viewer is still valid before accessing
    if (!isMountedRef.current || !isViewerValid(viewer) || !chart) return;
    
    const viewport = viewer.viewport;
    const tiledImage = viewer.world.getItemAt(0);
    if (!tiledImage || !viewport) return;
    
    // Get the current constraint bounds based on mode
    const mode = constraintModeRef.current;
    let constraintCrop;
    
    if (mode === 'fullChart') {
      constraintCrop = chart.fullChartBounds || { x: 0, y: 0, width: chart.width, height: chart.height };
    } else {
      // Arena mode - use mobileCrop on mobile, arenaCrop on desktop
      constraintCrop = (forMobile && chart.mobileCrop) ? chart.mobileCrop : chart.arenaCrop;
    }
    
    if (!constraintCrop) return;
    
    // Convert constraint crop to viewport coordinates
    const cb = tiledImage.imageToViewportRectangle(
      constraintCrop.x, constraintCrop.y, constraintCrop.width, constraintCrop.height
    );
    
    // Store for reference
    constraintBoundsRef.current = cb;
    
    // Get current viewport bounds
    const vp = viewport.getBounds();
    
    // Calculate how much the viewport TOP edge is above the crop TOP edge
    // If vpY < cbY, the viewport is showing content above the crop
    const aboveOverflow = Math.max(0, cb.y - vp.y);
    // Calculate how much the viewport BOTTOM edge is below the crop BOTTOM edge
    const belowOverflow = Math.max(0, (vp.y + vp.height) - (cb.y + cb.height));
    // Same for left/right
    const leftOverflow = Math.max(0, cb.x - vp.x);
    const rightOverflow = Math.max(0, (vp.x + vp.width) - (cb.x + cb.width));
    
    // Allow up to 20% of viewport height to show content above/below the crop
    // Allow up to 20% of viewport width to show content left/right of the crop
    const maxVerticalOverflow = vp.height * 0.20;
    const maxHorizontalOverflow = vp.width * 0.20;
    
    let needsCorrection = false;
    let newCenterX = vp.x + vp.width / 2;
    let newCenterY = vp.y + vp.height / 2;
    
    // Check vertical overflow
    if (aboveOverflow > maxVerticalOverflow) {
      // Viewport is showing too much content above the crop - move view down
      const correction = aboveOverflow - maxVerticalOverflow;
      newCenterY += correction;
      needsCorrection = true;
    } else if (belowOverflow > maxVerticalOverflow) {
      // Viewport is showing too much content below the crop - move view up
      const correction = belowOverflow - maxVerticalOverflow;
      newCenterY -= correction;
      needsCorrection = true;
    }
    
    // Check horizontal overflow
    if (leftOverflow > maxHorizontalOverflow) {
      const correction = leftOverflow - maxHorizontalOverflow;
      newCenterX += correction;
      needsCorrection = true;
    } else if (rightOverflow > maxHorizontalOverflow) {
      const correction = rightOverflow - maxHorizontalOverflow;
      newCenterX -= correction;
      needsCorrection = true;
    }
    
    if (needsCorrection) {
      viewport.panTo(new OpenSeadragon.Point(newCenterX, newCenterY), true);
    }
  }, []);

  // View controls - reset interaction flag when using Fit Arena
  const fitArena = useCallback(() => {
    // P0 FIX: Guard against invalid viewer state
    if (!isMountedRef.current || !isViewerValid(viewerRef.current)) return;
    const tiledImage = viewerRef.current.world.getItemAt(0);
    if (!tiledImage || !activeChart.arenaCrop) return;
    
    // Reset user interaction flag since they explicitly requested fit
    userHasInteracted.current = false;
    // Set constraint mode to arena
    constraintModeRef.current = 'arena';
    
    // Use mobileCrop for mobile devices if available
    const crop = (isMobile && activeChart.mobileCrop) ? activeChart.mobileCrop : activeChart.arenaCrop;
    const bounds = tiledImage.imageToViewportRectangle(crop.x, crop.y, crop.width, crop.height);
    
    // Store constraint bounds
    constraintBoundsRef.current = bounds;
    
    // Use double RAF for deterministic fitting
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // P0 FIX: Re-check validity after async wait
        if (isMountedRef.current && isViewerValid(viewerRef.current)) {
          viewerRef.current.viewport.fitBounds(bounds, false);
        }
      });
    });
    
    setSelectedZone(null);
    setSearchParams({ chart: activeChartId });
  }, [activeChart, activeChartId, isMobile, setSearchParams, isViewerValid]);

  const fitFullChart = useCallback(() => {
    // P0 FIX: Guard against invalid viewer state
    if (!isMountedRef.current || !isViewerValid(viewerRef.current)) return;
    const tiledImage = viewerRef.current.world.getItemAt(0);
    if (!tiledImage) return;
    
    // Set constraint mode to fullChart
    constraintModeRef.current = 'fullChart';
    
    const full = activeChart.fullChartBounds || { x: 0, y: 0, width: activeChart.width, height: activeChart.height };
    const bounds = tiledImage.imageToViewportRectangle(full.x, full.y, full.width, full.height);
    
    // Store constraint bounds
    constraintBoundsRef.current = bounds;
    
    requestAnimationFrame(() => {
      // P0 FIX: Re-check validity after async wait
      if (isMountedRef.current && isViewerValid(viewerRef.current)) {
        viewerRef.current.viewport.fitBounds(bounds, false);
      }
    });
    
    setSelectedZone(null);
    setSearchParams({ chart: activeChartId });
  }, [activeChart, activeChartId, setSearchParams]);

  // Helper: Apply arena fit bounds with proper timing
  const applyArenaFit = useCallback((viewer, chart, immediate = true, forMobile = false) => {
    if (!viewer || !chart?.arenaCrop) return;
    
    const tiledImage = viewer.world.getItemAt(0);
    if (!tiledImage) return;
    
    // Set constraint mode to arena
    constraintModeRef.current = 'arena';
    
    // Use mobileCrop for mobile devices if available, otherwise fall back to arenaCrop
    const crop = (forMobile && chart.mobileCrop) ? chart.mobileCrop : chart.arenaCrop;
    const bounds = tiledImage.imageToViewportRectangle(crop.x, crop.y, crop.width, crop.height);
    
    // Store constraint bounds
    constraintBoundsRef.current = bounds;
    
    // Use double RAF for deterministic fitting after layout settles
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (viewer.viewport) {
          viewer.viewport.fitBounds(bounds, immediate);
        }
      });
    });
  }, []);

  // Initialize OpenSeadragon - ONLY when in map view and container exists
  useEffect(() => {
    osdInitRunCountRef.current += 1;
    arenaLog('osd-effect-run', {
      runCount: osdInitRunCountRef.current,
      currentView,
      isMobile,
      retryToken: mapRetryToken,
      hasExistingViewer: !!viewerRef.current,
      containerExists: !!containerRef.current,
      containerWidth: containerRef.current?.clientWidth,
      containerHeight: containerRef.current?.clientHeight,
    });
    // Guard: Only initialize when in map view with a valid container
    if (currentView !== 'map') {
      // Ensure viewer is cleaned up when switching away from map view
      if (viewerRef.current) {
        try {
          // PROPER LIFECYCLE: close() aborts pending tile loads, then destroy()
          viewerRef.current.removeAllHandlers();
          viewerRef.current.close(); // Abort any in-flight image loads
          viewerRef.current.destroy();
          arenaLog('viewer-destroyed', { reason: 'currentView-not-map' });
        } catch (e) {
          // Ignore errors during cleanup
        }
        viewerRef.current = null;
      }
      return;
    }
    
    if (!containerRef.current) {
      arenaLog('osd-effect-abort', { reason: 'no-container' });
      return;
    }
    
    // P0 RECOVERY: container has zero dimensions - retry shortly instead of
    // silently creating a broken viewer (known OSD failure class)
    if (containerRef.current.clientWidth === 0 || containerRef.current.clientHeight === 0) {
      arenaLog('osd-effect-abort', {
        reason: 'zero-dimension-container',
        width: containerRef.current.clientWidth,
        height: containerRef.current.clientHeight,
      });
      const retryTimer = setTimeout(() => {
        if (isMountedRef.current) setMapRetryToken((t) => t + 1);
      }, 150);
      return () => clearTimeout(retryTimer);
    }
    
    // Skip if we already have a valid viewer that's not destroyed
    // Check by verifying the viewer can still render (has a container element)
    if (viewerRef.current) {
      try {
        // If viewer exists and still has a container, it's valid
        if (viewerRef.current.container && viewerRef.current.isOpen && viewerRef.current.isOpen()) {
          arenaLog('osd-effect-skip', { reason: 'existing-viewer-still-valid' });
          return;
        }
      } catch (e) {
        // Viewer is in invalid state, will recreate
      }
      // Clear invalid viewer reference
      viewerRef.current = null;
    }
    
    // Mobile gets higher minimum zoom to prevent tiny chart
    const minZoom = isMobile ? 0.7 : 0.3;
    // Mobile needs lower visibility ratio to allow tighter crop fitting
    const visRatio = isMobile ? 0.5 : 0.8;
    
    // Capture the current chart for use in handlers (avoid stale closure)
    const chartForInit = activeChart;
    // Capture isMobile state early for use in handlers
    const isMobileDevice = isMobile;
    
    // P0 FIX: Track if this effect instance is still valid
    let effectCleanedUp = false;
    // P0 ROOT-CAUSE FIX: React.StrictMode double-invokes this effect on mount
    // (mount -> cleanup -> mount) in development/Preview. Without deferring,
    // the FIRST (throwaway) pass starts a real OpenSeadragon instance + a full
    // network image download that gets destroyed almost instantly, while the
    // SECOND (surviving) instance starts its OWN competing download of the
    // exact same large image URL. This observed race is the root cause of the
    // intermittent blank/never-opens Arena map: the second viewer's 'open'
    // event can stall indefinitely while the orphaned first download (which
    // OpenSeadragon's close()/imageLoader.clear() does NOT abort once a job
    // has already started) contends for the same resource.
    // Fix: defer the actual expensive OpenSeadragon() creation by one animation
    // frame. StrictMode's synchronous mount->cleanup happens within the same
    // tick, well before a rAF callback ever fires, so the throwaway first pass
    // never creates a viewer or starts a download at all - only the real,
    // surviving mount does.
    let viewerToCleanup = null;
    let handleResize = null;
    let handleFullscreenChange = null;
    
    const deferredInitHandle = requestAnimationFrame(() => {
      if (effectCleanedUp) {
        arenaLog('osd-effect-deferred-skip', { reason: 'cleaned-up-before-init', chartUrl: chartForInit.url });
        return;
      }
      
      const osdViewer = OpenSeadragon({
        element: containerRef.current,
        tileSources: { type: 'image', url: chartForInit.url, buildPyramid: false },
        prefixUrl: 'https://cdnjs.cloudflare.com/ajax/libs/openseadragon/4.1.0/images/',
        showNavigator: !isMobileDevice, // Hide navigator on mobile
        navigatorPosition: 'BOTTOM_RIGHT',
        navigatorSizeRatio: 0.12,
        showNavigationControl: false,
        animationTime: 0.2,
        maxZoomPixelRatio: 4,
        minZoomLevel: minZoom,
        maxZoomLevel: 20,
        visibilityRatio: visRatio,
        constrainDuringPan: true,
        // CRITICAL: Disable default home behavior that zooms to full image
        homeFillsViewer: false,
        gestureSettingsMouse: { clickToZoom: false, dblClickToZoom: true, scrollToZoom: true },
        gestureSettingsTouch: { pinchToZoom: true, flickEnabled: true, dblClickToZoom: true },
        backgroundColor: '#ffffff'
      });
      
      viewerRef.current = osdViewer;
      viewerToCleanup = osdViewer;
      arenaLog('viewer-created', { chartUrl: chartForInit.url, containerW: containerRef.current.clientWidth, containerH: containerRef.current.clientHeight });
      
      // P0 RECOVERY: surface image/viewer open failures instead of leaving a blank canvas
      osdViewer.addOnceHandler('open-failed', (e) => {
        if (effectCleanedUp || !isMountedRef.current) return;
        arenaLog('viewer-open-failed', { chartUrl: chartForInit.url, message: e?.message });
        setMapLoadError('The arena map failed to load. Please retry.');
      });
      
      // Use addOnceHandler for the initial open to ensure it only fires once
      osdViewer.addOnceHandler('open', () => {
        // P0 FIX: Guard against stale callback after effect cleanup
        if (effectCleanedUp || !isMountedRef.current) return;
        arenaLog('viewer-opened', { chartUrl: chartForInit.url });
        setMapLoadError(null);
        
        // Track which chart URL we loaded
        loadedChartUrlRef.current = chartForInit.url;
        // Store current chart for constraint calculations
        currentChartRef.current = chartForInit;
        
        // Use double RAF to ensure shell layout has settled (toolbar, bottom nav, fonts)
        // This produces deterministic Fit Arena results across page loads
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            // P0 FIX: Re-verify validity after async operations
            if (effectCleanedUp || !isMountedRef.current || !isViewerValid(osdViewer)) return;
            applyArenaFit(osdViewer, chartForInit, true, isMobileDevice);
            setIsReady(true);
            
            // Initialize transform for marker overlay rendering
            const tiledImage = osdViewer.world.getItemAt(0);
            if (tiledImage) {
              try {
                const bounds = tiledImage.getBounds();
                const topLeft = osdViewer.viewport.viewportToViewerElementCoordinates(
                  new OpenSeadragon.Point(bounds.x, bounds.y)
                );
                const bottomRight = osdViewer.viewport.viewportToViewerElementCoordinates(
                  new OpenSeadragon.Point(bounds.x + bounds.width, bounds.y + bounds.height)
                );
                const imageWidth = tiledImage.source.width;
                const screenWidth = bottomRight.x - topLeft.x;
                const scale = screenWidth / imageWidth;
                setMapTransform({ scale, translateX: topLeft.x, translateY: topLeft.y });
              } catch (e) {
                console.error('Failed to initialize transform:', e);
              }
            }
          });
        });
      });
      
      // Track user interaction to prevent resize handler from resetting their view
      osdViewer.addHandler('canvas-drag', () => { userHasInteracted.current = true; });
      osdViewer.addHandler('canvas-pinch', () => { userHasInteracted.current = true; });
      
      // Update transform for SVG overlay rendering
      const updateTransform = () => {
        if (effectCleanedUp || !isMountedRef.current) return;
        const viewer = viewerRef.current;
        if (!viewer) return;
        
        const tiledImage = viewer.world.getItemAt(0);
        if (!tiledImage) return;
        
        try {
          const bounds = tiledImage.getBounds();
          const topLeft = viewer.viewport.viewportToViewerElementCoordinates(
            new OpenSeadragon.Point(bounds.x, bounds.y)
          );
          const bottomRight = viewer.viewport.viewportToViewerElementCoordinates(
            new OpenSeadragon.Point(bounds.x + bounds.width, bounds.y + bounds.height)
          );
          
          const imageWidth = tiledImage.source.width;
          const screenWidth = bottomRight.x - topLeft.x;
          const scale = screenWidth / imageWidth;
          
          setMapTransform({ scale, translateX: topLeft.x, translateY: topLeft.y });
        } catch (e) {
          // Ignore transform errors during transitions
        }
      };
      
      osdViewer.addHandler('zoom', (e) => {
        // P0 FIX: Guard zoom handler
        if (!effectCleanedUp && isMountedRef.current) {
          setCurrentZoom(e.zoom);
          updateTransform();
        }
      });
      
      // Update transform on pan as well
      osdViewer.addHandler('pan', updateTransform);
      osdViewer.addHandler('animation-finish', updateTransform);
      
      // Apply crop-aware constraints after pan/zoom on mobile
      // This prevents panning completely into blank space while allowing normal interaction
      // Use animation-finish which fires after any viewport animation completes
      const handleConstraints = () => {
        // P0 FIX: Guard constraint handler
        if (effectCleanedUp || !isMountedRef.current || !isViewerValid(osdViewer)) return;
        const currentlyMobile = window.innerWidth < 900;
        if (currentlyMobile && currentChartRef.current) {
          applyCropAwareConstraints(osdViewer, currentChartRef.current, currentlyMobile);
        }
      };
      osdViewer.addHandler('animation-finish', handleConstraints);
      // Also check on canvas-drag-end for non-animated pans
      osdViewer.addHandler('canvas-drag-end', handleConstraints);
      
      // Handle resize - only re-fit if user hasn't manually panned/zoomed
      handleResize = () => {
        // P0 FIX: Guard resize handler
        if (effectCleanedUp || !isMountedRef.current) return;
        const currentlyMobile = window.innerWidth < 900;
        if (!userHasInteracted.current && isViewerValid(viewerRef.current) && currentChartRef.current) {
          applyArenaFit(viewerRef.current, currentChartRef.current, false, currentlyMobile);
        } else if (isViewerValid(viewerRef.current) && currentChartRef.current) {
          // User has interacted - just apply constraints without resetting view
          applyCropAwareConstraints(viewerRef.current, currentChartRef.current, currentlyMobile);
        }
      };
      
      // Handle fullscreen changes - preserve valid viewport
      handleFullscreenChange = () => {
        // P0 FIX: Guard fullscreen handler
        if (effectCleanedUp || !isMountedRef.current) return;
        if (isViewerValid(viewerRef.current) && currentChartRef.current) {
          // Small delay to let viewport recalculate
          setTimeout(() => {
            // P0 FIX: Re-check validity after timeout
            if (effectCleanedUp || !isMountedRef.current || !isViewerValid(viewerRef.current)) return;
            const currentlyMobile = window.innerWidth < 900;
            applyCropAwareConstraints(viewerRef.current, currentChartRef.current, currentlyMobile);
          }, 100);
        }
      };
      
      window.addEventListener('resize', handleResize);
      window.addEventListener('orientationchange', handleResize);
      document.addEventListener('fullscreenchange', handleFullscreenChange);
    });
    
    return () => {
      // P0 FIX: Mark effect as cleaned up to prevent stale callbacks
      effectCleanedUp = true;
      // Cancel the deferred init if it hasn't fired yet (StrictMode throwaway pass)
      cancelAnimationFrame(deferredInitHandle);
      arenaLog('osd-effect-cleanup', { chartUrl: chartForInit.url, hadViewer: !!viewerToCleanup });
      
      if (handleResize) window.removeEventListener('resize', handleResize);
      if (handleResize) window.removeEventListener('orientationchange', handleResize);
      if (handleFullscreenChange) document.removeEventListener('fullscreenchange', handleFullscreenChange);
      
      // PROPER LIFECYCLE: Use captured viewer instance, not ref
      // close() aborts pending tile loads, then destroy()
      if (viewerToCleanup) {
        try {
          viewerToCleanup.removeAllHandlers();
          viewerToCleanup.close(); // Abort any in-flight image loads
          viewerToCleanup.destroy();
          arenaLog('viewer-destroyed', { reason: 'effect-cleanup', chartUrl: chartForInit.url });
        } catch (e) {
          // Ignore errors during cleanup (can happen during rapid navigation)
        }
        // Clear ref only if it still points to this viewer
        if (viewerRef.current === viewerToCleanup) {
          viewerRef.current = null;
        }
      }
    };
  }, [currentView, isMobile, applyArenaFit, applyCropAwareConstraints, isViewerValid, mapRetryToken]);

  // Handle chart changes (only when chart actually changes, not on initial ready)
  useEffect(() => {
    // P0 FIX: Guard against invalid viewer state
    if (!isMountedRef.current || !isViewerValid(viewerRef.current) || !isReady) return;
    
    // Skip if this chart is already loaded (prevents double-open on initial ready)
    if (loadedChartUrlRef.current === activeChart.url) {
      // Just update URL params without re-opening
      const newParams = { chart: activeChartId };
      if (selectedZone && JUMP_TARGETS[activeChartId]?.[selectedZone]) {
        newParams.zone = selectedZone;
      }
      setSearchParams(newParams);
      return;
    }
    
    // Reset user interaction flag on chart change
    userHasInteracted.current = false;
    // Reset constraint mode to arena for new chart
    constraintModeRef.current = 'arena';
    
    // P0 FIX: Track if this effect instance is still valid
    let chartEffectCleanedUp = false;
    
    viewerRef.current.open({ type: 'image', url: activeChart.url, buildPyramid: false });
    viewerRef.current.addOnceHandler('open', () => {
      // P0 FIX: Guard against stale callback
      if (chartEffectCleanedUp || !isMountedRef.current || !isViewerValid(viewerRef.current)) return;
      
      // Track which chart URL we loaded
      loadedChartUrlRef.current = activeChart.url;
      // Update current chart ref for constraint calculations
      currentChartRef.current = activeChart;
      
      // Use RAF for consistent timing with initial load
      requestAnimationFrame(() => {
        // P0 FIX: Re-verify validity after async wait
        if (chartEffectCleanedUp || !isMountedRef.current || !isViewerValid(viewerRef.current)) return;
        
        const tiledImage = viewerRef.current.world.getItemAt(0);
        if (tiledImage && activeChart.arenaCrop) {
          // Use mobileCrop for mobile devices if available
          const crop = (isMobile && activeChart.mobileCrop) ? activeChart.mobileCrop : activeChart.arenaCrop;
          const bounds = tiledImage.imageToViewportRectangle(crop.x, crop.y, crop.width, crop.height);
          
          // Store constraint bounds for the new chart
          constraintBoundsRef.current = bounds;
          
          viewerRef.current.viewport.fitBounds(bounds, true);
        }
        
        // If there's a valid zone for this chart, jump to it after chart loads
        if (selectedZone && JUMP_TARGETS[activeChartId]?.[selectedZone]) {
          setTimeout(() => {
            // P0 FIX: Guard against stale callback in timeout
            if (chartEffectCleanedUp || !isMountedRef.current) return;
            jumpToSection(selectedZone);
          }, 100);
        }
      });
    });
    
    // Update URL with current chart and valid zone
    const newParams = { chart: activeChartId };
    if (selectedZone && JUMP_TARGETS[activeChartId]?.[selectedZone]) {
      newParams.zone = selectedZone;
    }
    setSearchParams(newParams);
    
    // P0 FIX: Cleanup function to mark effect as stale
    return () => {
      chartEffectCleanedUp = true;
    };
  }, [activeChartId, activeChart, isReady, isMobile, selectedZone, isViewerValid]);

  // Handle initial zone from URL on first load
  useEffect(() => {
    if (!isReady || !isMountedRef.current) return;
    
    const urlZone = searchParams.get('zone');
    if (urlZone && !selectedZone) {
      // Small delay to ensure viewer is fully ready
      setTimeout(() => {
        // P0 FIX: Guard against stale callback
        if (!isMountedRef.current) return;
        jumpToSection(urlZone);
      }, 200);
    }
  }, [isReady]);

  // P0 Fix: ResizeObserver to handle panel collapse/expand without blanking the map
  useEffect(() => {
    const container = containerRef.current;
    if (!container || currentView !== 'map' || !isReady) return;

    let observerActive = true;
    
    const observer = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        // P0 FIX: Guard against stale callback
        if (!observerActive || !isMountedRef.current) return;
        
        const viewer = viewerRef.current;
        if (isViewerValid(viewer)) {
          try {
            viewer.viewport.resize();
            viewer.forceRedraw();
          } catch (e) {
            // Ignore resize errors during transitions
          }
        }
      });
    });

    observer.observe(container);
    return () => {
      observerActive = false;
      observer.disconnect();
    };
  }, [currentView, isReady, isViewerValid]);

  // Clear zone warning on reset/chart change/view switch
  useEffect(() => {
    if (currentView === 'list') {
      setJumpError(null);
    }
  }, [currentView]);

  return (
    <FullscreenContext.Provider value={portalLayerElement}>
      <div 
        ref={fullscreenRootRef}
        className={`ticket-registry-fullscreen-root ${isFullscreen ? 'is-fullscreen' : ''}`}
        data-testid="ticket-registry-root"
      >
        <Helmet>
          <title>{`${currentView === 'list' ? 'Ticket List' : 'Arena Map'} | Ticket Registry | Elvis Aloha Collection`}</title>
          <meta name="description" content="Explore the documented Elvis Aloha from Hawaii 1973 ticket registry by interactive arena seat map or full ticket list." />
          <link rel="canonical" href={window.location.href} />
        </Helmet>
        <div className="arena-page">
          {/* Desktop Sidebar - Hidden on Mobile */}
          <div className="arena-desktop-sidebar">
            <RegistrySidebar
              activeFilters={activeFilters}
              toggleFilter={toggleFilter}
              clearFilters={clearFilters}
              onJumpToSection={jumpToSection}
              ticketCount={allTickets.length}
              mappedCount={publishedOverlays.length}
              activeFilterCount={activeFilterCount}
              isMobile={false}
              sections={sectionLists}
              selectedZone={selectedZone}
              jumpError={jumpError}
              activeChartId={activeChartId}
            />
          </div>

          {/* Main Workspace */}
          <main className="arena-workspace">
            {/* Fixed Toolbar - Compact Mobile Design */}
            <header className="arena-toolbar">
              {/* Single Row: View Toggle, Filters, Jump, Chart Dropdown */}
              <div className="arena-toolbar__row">
                {/* View Mode Toggle - Use buttons to prevent remounting */}
                <div className="arena-view-toggle" data-testid="view-toggle">
                  <button 
                    type="button"
                    onClick={() => {
                      setCurrentView('map');
                      // Update URL to canonical route
                      window.history.replaceState(null, '', `/ticket-registry/map?${searchParams.toString()}`);
                    }}
                    className={`arena-view-toggle__btn ${currentView === 'map' ? 'arena-view-toggle__btn--active' : ''}`}
                    data-testid="view-toggle-map"
                  >
                    <Map size={18} />
                    <span>Map</span>
                  </button>
                  <button 
                    type="button"
                    onClick={() => {
                      setCurrentView('list');
                      // Update URL to canonical route
                      window.history.replaceState(null, '', `/ticket-registry/list?${searchParams.toString()}`);
                    }}
                    className={`arena-view-toggle__btn ${currentView === 'list' ? 'arena-view-toggle__btn--active' : ''}`}
                    data-testid="view-toggle-list"
                  >
                    <List size={18} />
                    <span>List</span>
                  </button>
                </div>

                {/* Filters Button (Mobile) */}
                <button 
                  type="button"
                  className="arena-toolbar__btn arena-toolbar__btn--icon"
                  onClick={() => setMobileMenuOpen(true)}
                  data-testid="mobile-filters-btn"
                  title="Filters"
                >
                  <Menu size={20} />
                  <span className="arena-toolbar__btn-label">Filters</span>
                </button>

                {/* Jump Button (Mobile) - Only show on Map view */}
                {currentView === 'map' && (
                  <button 
                    type="button"
                    className="arena-toolbar__btn arena-toolbar__btn--primary"
                    onClick={() => setJumpSheetOpen(true)}
                    data-testid="mobile-jump-btn"
                    title="Jump to Section"
                  >
                    <MapPin size={18} />
                    <span className="arena-toolbar__btn-label">Jump</span>
                  </button>
                )}

                {/* Chart Dropdown - Only show on Map view */}
                {currentView === 'map' && (
                  <div className="arena-chart-select">
                    <select
                      value={activeChartId}
                      onChange={(e) => setActiveChartId(e.target.value)}
                      data-testid="chart-selector"
                    >
                      {Object.values(CHARTS).map(chart => (
                        <option key={chart.id} value={chart.id}>
                          {chart.label}
                        </option>
                      ))}
                    </select>
                    <span className="arena-chart-select__icon">▾</span>
                  </div>
                )}

                {/* Spacer */}
                <div style={{ flex: 1 }} />

                {/* Desktop-only view controls in header - Only show on Map view */}
                {currentView === 'map' && (
                  <div className="arena-toolbar__desktop-controls">
                    <button type="button" onClick={fitArena} className="arena-toolbar__btn" data-testid="fit-arena-btn">
                      Fit Arena
                    </button>
                    <button type="button" onClick={fitFullChart} className="arena-toolbar__btn" data-testid="full-chart-btn">
                      Full Chart
                    </button>
                    <button type="button" onClick={fitArena} className="arena-toolbar__btn" data-testid="reset-btn">
                      <RotateCcw size={16} />
                      Reset
                    </button>
                    <button type="button" onClick={() => viewerRef.current?.viewport.zoomBy(1.5)} className="arena-toolbar__btn" data-testid="zoom-in-btn">
                      <Plus size={18} />
                  </button>
                  <button type="button" onClick={() => viewerRef.current?.viewport.zoomBy(0.67)} className="arena-toolbar__btn" data-testid="zoom-out-btn">
                    <Minus size={18} />
                  </button>
                  <button 
                    type="button"
                    onClick={toggleFullscreen}
                    className="arena-toolbar__btn"
                    data-testid="fullscreen-btn"
                  >
                    {isFullscreen ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
                  </button>
                </div>
                )}
              </div>
            </header>

            {/* Map Canvas - Only this moves/zooms - Only show on Map view */}
            {currentView === 'map' && (
              <div className="arena-canvas-shell">
                <div 
                  ref={containerRef}
                  className="arena-canvas"
                  data-testid="arena-canvas"
                />
                
                {/* P0 RECOVERY: Map load error state - never leave a blank canvas */}
                {mapLoadError && (
                  <div
                    data-testid="arena-map-error"
                    style={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '16px',
                      background: 'rgba(255,255,255,0.97)',
                      zIndex: 30,
                      textAlign: 'center',
                      padding: '24px',
                    }}
                  >
                    <p style={{ fontSize: '15px', color: '#333', maxWidth: '360px', margin: 0 }}>
                      {mapLoadError}
                    </p>
                    <button
                      type="button"
                      data-testid="arena-map-retry-btn"
                      onClick={() => {
                        setMapLoadError(null);
                        // Force viewer teardown + re-init on the next effect pass
                        if (viewerRef.current) {
                          try {
                            viewerRef.current.destroy();
                          } catch (e) { /* ignore */ }
                          viewerRef.current = null;
                        }
                        setMapRetryToken((t) => t + 1);
                      }}
                      style={{
                        padding: '10px 24px',
                        background: '#0D273C',
                        color: '#F7F1E5',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '13px',
                        fontWeight: 700,
                        letterSpacing: '0.05em',
                        textTransform: 'uppercase',
                        cursor: 'pointer',
                      }}
                    >
                      Retry
                    </button>
                  </div>
                )}
                
                {/* Exact seat geometry overlay - attached to OpenSeadragon */}
                {isReady && viewerRef.current && activeChart && (
                  <ExactSeatOverlay
                    viewer={viewerRef.current}
                    chartId={activeChartId}
                    chartDimensions={{
                      width: activeChart.fullChartBounds?.width || 2550,
                      height: activeChart.fullChartBounds?.height || 3300
                    }}
                    onSeatClick={(ticket, overlay) => {
                      // Canonical seats: only open the panel for assigned seats.
                      // Look up the FULL public ticket record (grade, form, collection
                      // label, etc.) via the assignment's public reference - the overlay
                      // itself only carries a partial, privacy-safe shape.
                      if (ticket) {
                        const fullTicket = allTickets.find(t =>
                          t.registry_reference === ticket.registry_reference || t.public_reference === ticket.registry_reference
                        ) || ticket;
                        setSelectedTicket(fullTicket);
                        setRightPanelOpen(true);
                        lastSelectedSeatElRef.current = overlay?.id || null;
                      }
                    }}
                    selectedTicketRef={selectedTicket?.registry_reference}
                  />
                )}
                
                {/* Mobile Navigation Strip - Floating above map */}
                {isMobile && (
                  <div className="mobile-nav-strip" data-testid="mobile-nav-strip">
                    <div className="mobile-nav-strip__controls">
                      <button 
                        type="button"
                        className="mobile-nav-strip__btn"
                        onClick={() => setJumpSheetOpen(true)}
                        data-testid="nav-jump-btn"
                      >
                        <MapPin size={16} />
                        <span>Jump to Section</span>
                      </button>
                      <button 
                        type="button"
                        className="mobile-nav-strip__btn mobile-nav-strip__btn--count"
                        onClick={() => setKnownTicketsOpen(true)}
                        data-testid="nav-tickets-btn"
                        aria-label={`${allTickets.length} documented tickets, ${publishedOverlays.length} mapped to a seat on this chart`}
                      >
                        <Ticket size={16} />
                        <span>{allTickets.length} documented · {publishedOverlays.length} mapped</span>
                      </button>
                    </div>
                    
                    {/* Active filter chips - only shown when filters active */}
                    {activeFilters.size > 0 && (
                      <div className="mobile-nav-strip__chips">
                        {Array.from(activeFilters).slice(0, 3).map(filterId => (
                          <button 
                            key={filterId}
                            type="button"
                            className="mobile-nav-strip__chip"
                            onClick={() => toggleFilter(filterId)}
                          >
                            {filterId.replace(/_/g, ' ')}
                            <X size={12} />
                          </button>
                        ))}
                        {activeFilters.size > 3 && (
                          <span className="mobile-nav-strip__more">+{activeFilters.size - 3}</span>
                        )}
                        <button 
                          type="button"
                          className="mobile-nav-strip__clear"
                          onClick={clearFilters}
                        >
                          Clear
                        </button>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Floating Map Controls (Mobile) */}
                <div className="arena-floating-controls">
                  <button 
                    type="button" 
                    onClick={fitArena} 
                    className="arena-floating-btn arena-floating-btn--text"
                    data-testid="mobile-fit-btn"
                    title="Fit Arena"
                  >
                    <span>Fit Arena</span>
                  </button>
                  <button 
                    type="button" 
                    onClick={() => viewerRef.current?.viewport.zoomBy(1.5)} 
                    className="arena-floating-btn"
                    data-testid="mobile-zoom-in"
                    title="Zoom In"
                  >
                    <Plus size={20} />
                  </button>
                  <button 
                    type="button" 
                    onClick={() => viewerRef.current?.viewport.zoomBy(0.67)} 
                    className="arena-floating-btn"
                    data-testid="mobile-zoom-out"
                    title="Zoom Out"
                  >
                    <Minus size={20} />
                  </button>
                  <button 
                    type="button"
                    onClick={toggleFullscreen}
                    className="arena-floating-btn"
                    data-testid="mobile-fullscreen"
                    title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
                  >
                    {isFullscreen ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
                  </button>
                </div>
              </div>
            )}

            {/* List View - Data table for research */}
            {currentView === 'list' && (
              <div className="arena-list-shell" data-testid="list-view-shell">
                <TicketListView
                  tickets={allTickets}
                  filters={activeFilters}
                  onFilterChange={toggleFilter}
                  onNavigateToMap={(ticket) => {
                    // Navigate to map view with the ticket's section highlighted
                    const params = new URLSearchParams(searchParams);
                    if (ticket.section) {
                      params.set('zone', ticket.section.toLowerCase());
                    }
                    if (ticket.registry_reference) {
                      params.set('ticket', ticket.registry_reference);
                    }
                    navigate(`/ticket-registry/map?${params.toString()}`);
                  }}
                  onSelectTicket={setSelectedTicket}
                  selectedTicket={selectedTicket}
                  isLoading={ticketsLoading}
                />
              </div>
            )}

            {/* Fixed Status Bar - Adapts to current view */}
            <footer className="arena-statusbar" data-testid="arena-statusbar">
              {currentView === 'map' ? (
                <>
                  <span className="arena-statusbar__chart">{activeChart.label}</span>
                  <span className="arena-statusbar__zoom">Zoom: {Math.round(currentZoom * 100)}%</span>
                  {selectedZone && chartTargets?.[selectedZone] && (
                    <span className="arena-statusbar__zone">
                      <MapPin size={14} />
                      {chartTargets[selectedZone].label}
                    </span>
                  )}
                  <span className="arena-statusbar__tickets">
                    {allTickets.length} documented · {publishedOverlays.length} mapped
                  </span>
                </>
              ) : (
                <>
                  <span className="arena-statusbar__view">List View</span>
                  <span className="arena-statusbar__tickets">
                    {ticketsLoading ? 'Loading...' : `${allTickets.length} documented tickets`}
                  </span>
                  {activeFilters.size > 0 && (
                    <span className="arena-statusbar__filters">{activeFilters.size} filters active</span>
                  )}
                </>
              )}
            </footer>
          </main>

          {/* Desktop Right Panel - Ticket Results (Map view only) */}
          {!isMobile && currentView === 'map' && (
            <TicketResultsPanel
              tickets={publishedOverlays}
              selectedTicket={selectedTicket}
              onSelectTicket={handleSelectTicket}
              onZoomToTicket={(ticket) => {
                // Zoom to ticket seat and expand panel
                if (ticket?.seat_info?.zone) {
                  jumpToSection(ticket.seat_info.zone);
                }
                handleSelectTicket(ticket);
              }}
              isOpen={rightPanelOpen}
              onToggle={toggleRightPanel}
              activeFilters={activeFilters}
              filterLabels={[]}
              onClearFilters={clearFilters}
            />
          )}
        </div>

        {/* Portal Layer - All sheets, modals, dropdowns render here */}
        <div 
          ref={portalLayerRefCallback}
          className="registry-portal-layer"
          data-testid="portal-layer"
        />

        {/* Mobile Filters Drawer - Uses Portal */}
        <MobileFiltersDrawer 
          isOpen={mobileMenuOpen} 
          onClose={() => setMobileMenuOpen(false)}
        >
          <RegistrySidebar
            activeFilters={activeFilters}
            toggleFilter={toggleFilter}
            clearFilters={clearFilters}
            onJumpToSection={(zoneId) => { jumpToSection(zoneId); setMobileMenuOpen(false); }}
            ticketCount={allTickets.length}
            mappedCount={publishedOverlays.length}
            activeFilterCount={activeFilterCount}
            isMobile={true}
            onClose={() => setMobileMenuOpen(false)}
            sections={sectionLists}
            selectedZone={selectedZone}
            jumpError={jumpError}
            activeChartId={activeChartId}
          />
        </MobileFiltersDrawer>

        {/* Jump to Section Bottom Sheet - Uses Portal */}
        <JumpToSectionSheet
          isOpen={jumpSheetOpen}
          onClose={() => setJumpSheetOpen(false)}
          sections={sectionLists}
          chartTargets={chartTargets}
          selectedZone={selectedZone}
          onJump={(zoneId) => { jumpToSection(zoneId); setJumpSheetOpen(false); }}
        />

        {/* Known Tickets Sheet (Mobile) - Uses Portal */}
        <KnownTicketsSheet
          isOpen={knownTicketsOpen}
          onClose={() => setKnownTicketsOpen(false)}
          tickets={publishedOverlays}
          onSelectTicket={setSelectedTicket}
          selectedTicket={selectedTicket}
        />

        {/* Ticket Detail Bottom Sheet (Mobile only) - opens when a public
            visitor taps an assigned seat on the map. Desktop keeps the
            existing side panel instead (see TicketResultsPanel below). */}
        {isMobile && (
          <TicketDetailSheet
            isOpen={rightPanelOpen && !!selectedTicket}
            onClose={closeTicketDetail}
            ticket={selectedTicket}
          />
        )}

      {/* Arena Styles - Fullscreen-aware */}
      <style>{`
        /* Fullscreen Root - Contains entire application */
        .ticket-registry-fullscreen-root {
          position: relative;
          width: 100%;
          height: 100%;
          background: #F7F1E5;
          isolation: isolate; /* Creates stacking context for all children */
        }
        
        .ticket-registry-fullscreen-root.is-fullscreen {
          background: #1a1a1a;
        }
        
        /* Portal layer for sheets/modals - absolute positioned inside fullscreen root */
        .registry-portal-layer {
          position: absolute;
          inset: 0;
          z-index: 9000;
          pointer-events: none;
          /* Ensures portal layer is on top of everything */
        }
        
        .registry-portal-layer > * {
          pointer-events: auto;
        }
        
        /* Full viewport fixed shell */
        .arena-page {
          height: 100%;
          width: 100%;
          overflow: hidden;
          display: flex;
          background: #F7F1E5;
          position: relative;
          z-index: 1;
        }
        
        .is-fullscreen .arena-page {
          height: 100%;
          background: #1a1a1a;
        }
        
        /* Desktop sidebar */
        .arena-desktop-sidebar {
          display: block;
          flex-shrink: 0;
          position: relative;
          z-index: 50;
        }
        
        /* Main workspace - fills remaining space */
        .arena-workspace {
          flex: 1;
          display: flex;
          flex-direction: column;
          min-width: 0;
          position: relative;
          background: #ffffff;
          z-index: 1;
        }
        
        /* Compact toolbar */
        .arena-toolbar {
          flex-shrink: 0;
          background: #F7F1E5;
          border-bottom: 1px solid rgba(181, 138, 60, 0.28);
          padding: 10px 12px;
          z-index: 50;
        }
        
        .arena-toolbar__row {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        /* Toolbar buttons */
        .arena-toolbar__btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 10px 14px;
          background: #FFFFFF;
          border: 1px solid rgba(11, 45, 69, 0.15);
          border-radius: 8px;
          font-size: 13px;
          font-weight: 500;
          font-family: 'Work Sans', system-ui, sans-serif;
          color: #0B2D45;
          cursor: pointer;
          min-height: 44px;
          white-space: nowrap;
          transition: background-color 0.15s, transform 0.1s;
        }
        
        .arena-toolbar__btn:hover {
          background: #F7F1E5;
        }
        
        .arena-toolbar__btn:active {
          transform: scale(0.98);
        }
        
        .arena-toolbar__btn--icon {
          padding: 10px 12px;
        }
        
        .arena-toolbar__btn--primary {
          background: #C9A962;
          color: #FFFFFF;
          border-color: #C9A962;
        }
        
        .arena-toolbar__btn--primary:hover {
          background: #B8983F;
        }
        
        /* Chart Dropdown Select */
        .arena-chart-select {
          position: relative;
          display: flex;
          align-items: center;
        }
        
        .arena-chart-select select {
          appearance: none;
          background: #FFFFFF;
          border: 1px solid rgba(11, 45, 69, 0.15);
          border-radius: 8px;
          padding: 10px 36px 10px 14px;
          font-size: 13px;
          font-weight: 500;
          font-family: 'Work Sans', system-ui, sans-serif;
          color: #0B2D45;
          cursor: pointer;
          min-height: 44px;
          min-width: 140px;
        }
        
        .arena-chart-select select:focus {
          outline: 2px solid #C9A962;
          outline-offset: 1px;
        }
        
        .arena-chart-select__icon {
          position: absolute;
          right: 12px;
          pointer-events: none;
          color: #0B2D45;
          font-size: 12px;
        }
        
        /* Desktop controls in header */
        .arena-toolbar__desktop-controls {
          display: flex;
          gap: 6px;
        }
        
        /* View Toggle (Map / List) */
        .arena-view-toggle {
          display: flex;
          align-items: center;
          background: rgba(11, 45, 69, 0.08);
          border-radius: 8px;
          padding: 3px;
          gap: 2px;
        }
        
        .arena-view-toggle__btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 14px;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 500;
          color: #666;
          text-decoration: none;
          transition: all 0.15s ease;
        }
        
        .arena-view-toggle__btn:hover {
          color: #0B2D45;
          background: rgba(255, 255, 255, 0.5);
        }
        
        .arena-view-toggle__btn--active {
          background: #ffffff;
          color: #0B2D45;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }
        
        .arena-view-toggle__btn svg {
          flex-shrink: 0;
        }
        
        /* Floating Map Controls (Mobile) */
        .arena-floating-controls {
          display: none;
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          flex-direction: column;
          gap: 8px;
          z-index: 40;
        }
        
        .arena-floating-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 44px;
          height: 44px;
          background: rgba(255, 255, 255, 0.95);
          border: 1px solid rgba(11, 45, 69, 0.15);
          border-radius: 10px;
          color: #0B2D45;
          cursor: pointer;
          font-size: 12px;
          font-weight: 600;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          transition: background-color 0.15s, transform 0.1s;
        }
        
        .arena-floating-btn:hover {
          background: #FFFFFF;
        }
        
        .arena-floating-btn:active {
          transform: scale(0.95);
        }
        
        .arena-floating-btn--text {
          width: auto;
          padding: 10px 14px;
          font-size: 11px;
        }
        
        /* Canvas shell */
        .arena-canvas-shell {
          flex: 1;
          min-height: 0;
          position: relative;
          overflow: hidden;
          background: #ffffff;
          touch-action: none;
        }
        
        .arena-canvas {
          position: absolute;
          inset: 0;
          touch-action: none;
          user-select: none;
        }
        
        /* List view shell */
        .arena-list-shell {
          flex: 1;
          min-height: 0;
          overflow: hidden;
          background: #121212;
          display: flex;
          flex-direction: column;
        }
        
        /* ========================================
           MOBILE NAVIGATION STRIP
           Floating above map, below toolbar
           ======================================== */
        .mobile-nav-strip {
          position: absolute;
          top: 8px;
          left: 8px;
          right: 8px;
          z-index: 100;
          display: flex;
          flex-direction: column;
          gap: 8px;
          pointer-events: none;
        }
        
        .mobile-nav-strip__controls {
          display: flex;
          gap: 8px;
          pointer-events: auto;
        }
        
        .mobile-nav-strip__btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 10px 14px;
          background: rgba(255, 255, 255, 0.95);
          border: 1px solid rgba(11, 45, 69, 0.15);
          border-radius: 8px;
          font-size: 13px;
          font-weight: 500;
          color: #0B2D45;
          cursor: pointer;
          backdrop-filter: blur(8px);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
          transition: all 0.15s ease;
        }
        
        .mobile-nav-strip__btn:active {
          transform: scale(0.97);
          background: rgba(255, 255, 255, 1);
        }
        
        .mobile-nav-strip__btn--count {
          background: rgba(201, 169, 98, 0.1);
          border-color: rgba(201, 169, 98, 0.3);
        }
        
        .mobile-nav-strip__chips {
          display: flex;
          gap: 6px;
          overflow-x: auto;
          padding-bottom: 4px;
          scrollbar-width: none;
          -webkit-overflow-scrolling: touch;
          pointer-events: auto;
        }
        
        .mobile-nav-strip__chips::-webkit-scrollbar {
          display: none;
        }
        
        .mobile-nav-strip__chip {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 6px 10px;
          background: rgba(11, 45, 69, 0.08);
          border: none;
          border-radius: 16px;
          font-size: 11px;
          color: #0B2D45;
          cursor: pointer;
          white-space: nowrap;
          flex-shrink: 0;
        }
        
        .mobile-nav-strip__chip:hover {
          background: rgba(11, 45, 69, 0.12);
        }
        
        .mobile-nav-strip__more {
          display: inline-flex;
          align-items: center;
          padding: 6px 8px;
          font-size: 11px;
          color: #666;
        }
        
        .mobile-nav-strip__clear {
          display: inline-flex;
          align-items: center;
          padding: 6px 12px;
          background: #0B2D45;
          border: none;
          border-radius: 16px;
          font-size: 11px;
          color: #fff;
          cursor: pointer;
          white-space: nowrap;
          flex-shrink: 0;
        }
        
        /* Status bar */
        .arena-statusbar {
          flex-shrink: 0;
          background: #F7F1E5;
          border-top: 1px solid rgba(181, 138, 60, 0.28);
          padding: 8px 16px;
          display: flex;
          align-items: center;
          gap: 16px;
          font-size: 12px;
          font-family: 'Work Sans', system-ui, sans-serif;
          color: #0B2D45;
          z-index: 50;
        }
        
        .arena-statusbar__zone {
          display: flex;
          align-items: center;
          gap: 4px;
          color: #C9A962;
          font-weight: 600;
        }
        
        .arena-statusbar__tickets {
          margin-left: auto;
        }
        
        .arena-statusbar__view {
          font-weight: 600;
          color: #C9A962;
        }
        
        .arena-statusbar__filters {
          color: #888;
          font-size: 11px;
        }
        
        /* ========================================
           RIGHT PANEL - TICKET RESULTS SHELL
           ======================================== */
        .ticket-results-shell {
          position: relative;
          width: 360px;
          flex: 0 0 360px;
          background: #ffffff;
          border-left: 1px solid rgba(11, 45, 69, 0.1);
          display: none; /* Hidden on mobile */
          height: 100%;
          transition: width 180ms ease, flex-basis 180ms ease;
          z-index: 50;
        }
        
        .ticket-results-shell.is-collapsed {
          width: 48px;
          flex-basis: 48px;
        }
        
        /* Persistent toggle button - always visible on left edge */
        .ticket-results-toggle {
          position: absolute;
          left: -18px;
          top: 50%;
          transform: translateY(-50%);
          z-index: 60;
          width: 36px;
          height: 52px;
          background: #F7F1E5;
          border: 1px solid rgba(11, 45, 69, 0.15);
          border-radius: 8px 0 0 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          color: #0B2D45;
          transition: background 0.15s, color 0.15s;
          box-shadow: -2px 0 8px rgba(0, 0, 0, 0.05);
        }
        
        .ticket-results-toggle:hover {
          background: #e8dcc8;
        }
        
        /* Panel content */
        .ticket-results-panel {
          display: flex;
          flex-direction: column;
          height: 100%;
          width: 100%;
        }
        
        .ticket-results-panel__header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px;
          border-bottom: 1px solid rgba(11, 45, 69, 0.1);
          background: #F7F1E5;
        }
        
        .ticket-results-panel__title {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
          font-weight: 600;
          color: #0B2D45;
        }
        
        .ticket-results-panel__count {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 24px;
          height: 24px;
          padding: 0 8px;
          background: #C9A962;
          color: #fff;
          font-size: 12px;
          font-weight: 700;
          border-radius: 12px;
        }
        
        .ticket-results-panel__filters {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          padding: 12px 16px;
          border-bottom: 1px solid rgba(11, 45, 69, 0.08);
        }
        
        .ticket-results-panel__list {
          flex: 1;
          overflow-y: auto;
          padding: 12px;
        }
        
        .ticket-results-panel__empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 12px;
          padding: 48px 24px;
          text-align: center;
          color: #888;
        }
        
        .ticket-results-panel__empty p {
          margin: 0;
          font-size: 14px;
          line-height: 1.5;
        }
        
        .ticket-results-panel__clear {
          padding: 8px 16px;
          background: #0B2D45;
          color: #fff;
          border: none;
          border-radius: 6px;
          font-size: 13px;
          cursor: pointer;
        }
        
        .ticket-results-panel__clear:hover {
          background: #163d5c;
        }
        
        /* Collapsed rail - visible when panel is collapsed */
        .ticket-results-collapsed-rail {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 16px 8px;
          height: 100%;
        }
        
        .ticket-results-collapsed-rail__count {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 28px;
          height: 28px;
          padding: 0 8px;
          background: #C9A962;
          color: #fff;
          font-size: 12px;
          font-weight: 700;
          border-radius: 14px;
        }
        
        /* ========================================
           TICKET CARD
           ======================================== */
        .ticket-card {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 12px;
          background: #ffffff;
          border: 1px solid rgba(11, 45, 69, 0.1);
          border-radius: 8px;
          margin-bottom: 8px;
          cursor: pointer;
          transition: border-color 0.15s, box-shadow 0.15s;
        }
        
        .ticket-card:hover {
          border-color: #C9A962;
          box-shadow: 0 2px 8px rgba(201, 169, 98, 0.15);
        }
        
        .ticket-card--selected {
          border-color: #C9A962;
          background: rgba(201, 169, 98, 0.06);
          box-shadow: 0 0 0 2px rgba(201, 169, 98, 0.3);
        }
        
        .ticket-card__thumb {
          width: 64px;
          height: 64px;
          flex-shrink: 0;
          border-radius: 6px;
          overflow: hidden;
          background: #f5f5f5;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .ticket-card__thumb img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        
        .ticket-card__thumb-placeholder {
          color: #ccc;
        }
        
        .ticket-card__info {
          flex: 1;
          min-width: 0;
        }
        
        .ticket-card__seat {
          display: flex;
          align-items: center;
          gap: 4px;
          font-family: 'JetBrains Mono', ui-monospace, monospace;
          font-size: 13px;
          color: #0B2D45;
          margin-bottom: 4px;
        }
        
        .ticket-card__seat-label {
          color: #888;
          font-size: 10px;
          text-transform: uppercase;
        }
        
        .ticket-card__seat-value {
          font-weight: 600;
        }
        
        .ticket-card__seat-sep {
          color: #ccc;
        }
        
        .ticket-card__type {
          font-size: 13px;
          color: #0B2D45;
          margin-bottom: 4px;
        }
        
        .ticket-card__grading {
          color: #C9A962;
          font-weight: 600;
        }
        
        .ticket-card__collection {
          font-size: 11px;
          color: #666;
          margin-bottom: 4px;
        }
        
        .ticket-card__tags {
          display: flex;
          flex-wrap: wrap;
          gap: 4px;
        }
        
        .ticket-card__tag {
          display: inline-block;
          padding: 2px 8px;
          background: rgba(11, 45, 69, 0.08);
          border-radius: 4px;
          font-size: 10px;
          color: #0B2D45;
        }
        
        .ticket-card__zoom {
          flex-shrink: 0;
          background: none;
          border: none;
          color: #888;
          cursor: pointer;
          padding: 4px;
          border-radius: 4px;
        }
        
        .ticket-card__zoom:hover {
          color: #C9A962;
          background: rgba(201, 169, 98, 0.1);
        }
        
        /* Desktop: Show right panel shell */
        @media (min-width: 900px) {
          .ticket-results-shell {
            display: block;
          }
        }
        
        /* Mobile Bottom Navigation */
        .arena-mobile-nav {
          display: none;
          position: fixed;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 200;
          grid-template-columns: repeat(5, 1fr);
          min-height: calc(64px + env(safe-area-inset-bottom, 0px));
          padding-bottom: env(safe-area-inset-bottom, 0px);
          background: #F7F1E5;
          border-top: 1px solid rgba(181, 138, 60, 0.35);
          box-shadow: 0 -8px 24px rgba(11, 45, 69, 0.08);
        }
        
        .arena-mobile-nav__item {
          min-height: 64px;
          padding: 8px 4px 6px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 4px;
          color: #0B2D45;
          text-decoration: none;
          font-size: 11px;
          font-weight: 600;
          font-family: 'Work Sans', system-ui, sans-serif;
          letter-spacing: 0.02em;
          background: none;
          border: none;
          cursor: pointer;
        }
        
        .arena-mobile-nav__item--active {
          color: #D85C4A;
        }
        
        /* Mobile drawer */
        .arena-mobile-drawer-overlay {
          position: absolute;
          inset: 0;
          background: rgba(0,0,0,0.5);
          z-index: 9100;
        }
        
        .arena-mobile-drawer {
          position: absolute;
          top: 0;
          left: 0;
          bottom: 0;
          width: 300px;
          max-width: 85vw;
          overflow-y: auto;
          z-index: 9101;
        }
        
        .arena-mobile-drawer__close {
          position: absolute;
          top: 16px;
          right: 16px;
          background: rgba(0, 0, 0, 0.6);
          border: none;
          color: white;
          font-size: 28px;
          cursor: pointer;
          z-index: 100;
          width: 44px;
          height: 44px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 8px;
          transition: background 0.15s;
        }
        
        .arena-mobile-drawer__close:hover {
          background: rgba(0, 0, 0, 0.8);
        }
        
        /* Desktop defaults */
        .arena-toolbar__btn-label { display: inline; }
        
        /* MOBILE BREAKPOINT */
        @media (max-width: 899px) {
          .arena-page {
            padding-bottom: calc(64px + env(safe-area-inset-bottom, 0px));
          }
          
          .arena-desktop-sidebar {
            display: none !important;
          }
          
          .arena-toolbar__desktop-controls {
            display: none;
          }
          
          .arena-floating-controls {
            display: flex;
          }
          
          .arena-mobile-nav {
            display: grid;
          }
          
          .arena-toolbar {
            padding: 8px 10px;
          }
          
          .arena-toolbar__btn {
            padding: 8px 12px;
            min-height: 42px;
          }
          
          .arena-chart-select select {
            min-width: 110px;
            padding: 8px 30px 8px 10px;
            font-size: 12px;
          }
          
          .arena-statusbar {
            padding: 6px 12px;
            font-size: 11px;
            gap: 10px;
          }
          
          .arena-statusbar__chart { display: none; }
          
          /* Hide OpenSeadragon navigator on mobile */
          .navigator {
            display: none !important;
          }
        }
        
        /* Very small mobile - hide button labels */
        @media (max-width: 380px) {
          .arena-toolbar__btn-label {
            display: none;
          }
          
          .arena-toolbar__btn--icon {
            padding: 8px 10px;
          }
          
          .arena-chart-select select {
            min-width: 95px;
            font-size: 11px;
          }
        }
        
        /* ======================================
           SHEET STYLES - Portal Layer Components
           ====================================== */
        
        /* Jump to Section Sheet */
        .jump-sheet-overlay {
          position: absolute;
          inset: 0;
          background: rgba(0, 0, 0, 0.6);
          z-index: 9100;
          display: flex;
          align-items: flex-end;
          justify-content: center;
        }
        
        .jump-sheet {
          background: #1A1A1A;
          width: 100%;
          max-width: 500px;
          max-height: 70vh;
          border-radius: 20px 20px 0 0;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          animation: slideUp 0.25s ease-out;
          z-index: 9101;
        }
        
        @keyframes slideUp {
          from { transform: translateY(100%); opacity: 0.8; }
          to { transform: translateY(0); opacity: 1; }
        }
        
        .jump-sheet__header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px 20px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }
        
        .jump-sheet__title {
          display: flex;
          align-items: center;
          gap: 10px;
          color: #FFFFFF;
          font-size: 16px;
          font-weight: 600;
          font-family: 'Work Sans', system-ui, sans-serif;
        }
        
        .jump-sheet__title svg {
          color: #C9A962;
        }
        
        .jump-sheet__close {
          background: none;
          border: none;
          color: #888;
          cursor: pointer;
          padding: 8px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.15s, color 0.15s;
        }
        
        .jump-sheet__close:hover {
          background: rgba(255, 255, 255, 0.1);
          color: #FFF;
        }
        
        .jump-sheet__search {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 20px;
          background: rgba(255, 255, 255, 0.05);
          border-bottom: 1px solid rgba(255, 255, 255, 0.08);
        }
        
        .jump-sheet__search svg {
          color: #666;
          flex-shrink: 0;
        }
        
        .jump-sheet__search input {
          flex: 1;
          background: none;
          border: none;
          color: #FFF;
          font-size: 15px;
          font-family: 'Work Sans', system-ui, sans-serif;
          outline: none;
        }
        
        .jump-sheet__search input::placeholder {
          color: #666;
        }
        
        .jump-sheet__content {
          flex: 1;
          overflow-y: auto;
          padding: 8px 0;
        }
        
        .jump-sheet__group {
          padding: 8px 0;
        }
        
        .jump-sheet__group-label {
          padding: 8px 20px;
          color: #888;
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }
        
        .jump-sheet__group-items {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          padding: 4px 20px 8px;
        }
        
        .jump-sheet__item {
          padding: 10px 16px;
          background: rgba(255, 255, 255, 0.08);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 8px;
          color: #FFF;
          font-size: 13px;
          font-weight: 500;
          font-family: 'Work Sans', system-ui, sans-serif;
          cursor: pointer;
          transition: background 0.15s, border-color 0.15s;
        }
        
        .jump-sheet__item:hover {
          background: rgba(255, 255, 255, 0.12);
          border-color: rgba(255, 255, 255, 0.2);
        }
        
        .jump-sheet__item--active {
          background: rgba(201, 169, 98, 0.2);
          border-color: #C9A962;
          color: #C9A962;
        }
        
        .jump-sheet__empty {
          padding: 32px 20px;
          text-align: center;
          color: #666;
          font-size: 14px;
        }
        
        /* Known Tickets Sheet */
        .known-tickets-overlay {
          position: absolute;
          inset: 0;
          background: rgba(0, 0, 0, 0.6);
          z-index: 9100;
          display: flex;
          align-items: flex-end;
          justify-content: center;
        }
        
        .known-tickets-sheet {
          background: #1A1A1A;
          width: 100%;
          max-width: 500px;
          max-height: 70vh;
          border-radius: 20px 20px 0 0;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          animation: slideUp 0.25s ease-out;
          z-index: 9101;
        }
        
        .known-tickets-sheet__header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px 20px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }
        
        .known-tickets-sheet__title {
          display: flex;
          align-items: center;
          gap: 10px;
          color: #FFFFFF;
          font-size: 16px;
          font-weight: 600;
          font-family: 'Work Sans', system-ui, sans-serif;
        }
        
        .known-tickets-sheet__title svg {
          color: #C9A962;
        }
        
        .known-tickets-sheet__count {
          background: rgba(201, 169, 98, 0.2);
          color: #C9A962;
          padding: 2px 8px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 600;
        }
        
        .known-tickets-sheet__close {
          background: none;
          border: none;
          color: #888;
          cursor: pointer;
          padding: 8px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.15s, color 0.15s;
        }
        
        .known-tickets-sheet__close:hover {
          background: rgba(255, 255, 255, 0.1);
          color: #FFF;
        }
        
        .known-tickets-sheet__content {
          flex: 1;
          overflow-y: auto;
          padding: 8px 0;
        }
        
        .known-tickets-sheet__empty {
          padding: 48px 20px;
          text-align: center;
          color: #666;
        }
        
        .known-tickets-sheet__empty svg {
          opacity: 0.4;
          margin-bottom: 12px;
        }
        
        .known-tickets-sheet__empty p {
          font-size: 14px;
          margin: 0;
        }
        
        /* Ticket Detail Sheet - mobile bottom sheet for a single assigned seat */
        .ticket-detail-sheet-overlay {
          position: absolute;
          inset: 0;
          background: rgba(0, 0, 0, 0.6);
          z-index: 9200;
          display: flex;
          align-items: flex-end;
          justify-content: center;
        }
        
        .ticket-detail-sheet {
          background: #1A1A1A;
          width: 100%;
          max-width: 500px;
          max-height: 75vh;
          border-radius: 20px 20px 0 0;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          animation: slideUp 0.25s ease-out;
          z-index: 9201;
          padding-bottom: env(safe-area-inset-bottom, 0px);
        }
        
        .ticket-detail-sheet__handle {
          width: 40px;
          height: 4px;
          background: rgba(255, 255, 255, 0.25);
          border-radius: 2px;
          margin: 10px auto 0;
          flex-shrink: 0;
        }
        
        .ticket-detail-sheet__header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 20px 16px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          flex-shrink: 0;
        }
        
        .ticket-detail-sheet__title {
          display: flex;
          align-items: center;
          gap: 10px;
          color: #FFFFFF;
          font-size: 16px;
          font-weight: 700;
          font-family: 'Work Sans', system-ui, sans-serif;
          letter-spacing: 0.02em;
        }
        
        .ticket-detail-sheet__title svg {
          color: #C9A962;
        }
        
        .ticket-detail-sheet__close {
          background: none;
          border: none;
          color: #888;
          cursor: pointer;
          padding: 8px;
          min-width: 44px;
          min-height: 44px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.15s, color 0.15s;
        }
        
        .ticket-detail-sheet__close:hover {
          background: rgba(255, 255, 255, 0.1);
          color: #FFF;
        }
        
        .ticket-detail-sheet__content {
          flex: 1;
          overflow-y: auto;
          padding: 18px 20px 28px;
        }
        
        .ticket-detail-sheet__seat-row {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-bottom: 18px;
        }
        
        .ticket-detail-sheet__seat-pill {
          background: rgba(201, 169, 98, 0.18);
          color: #C9A962;
          padding: 6px 12px;
          border-radius: 999px;
          font-size: 13px;
          font-weight: 700;
        }
        
        .ticket-detail-sheet__field {
          display: flex;
          justify-content: space-between;
          gap: 12px;
          padding: 10px 0;
          border-bottom: 1px solid rgba(255, 255, 255, 0.08);
        }
        
        .ticket-detail-sheet__label {
          color: #999;
          font-size: 13px;
          font-weight: 600;
        }
        
        .ticket-detail-sheet__value {
          color: #F0EAD9;
          font-size: 13px;
          font-weight: 500;
          text-align: right;
        }
        
        .ticket-detail-sheet__tags {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-top: 16px;
        }
        
        .ticket-detail-sheet__tag {
          background: rgba(255, 255, 255, 0.08);
          color: #D8D0BE;
          padding: 5px 10px;
          border-radius: 999px;
          font-size: 12px;
        }
        
        /* Known Ticket Item */
        .known-ticket-item {
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 14px 20px;
          cursor: pointer;
          transition: background 0.15s;
        }
        
        .known-ticket-item:hover {
          background: rgba(255, 255, 255, 0.05);
        }
        
        .known-ticket-item--selected {
          background: rgba(201, 169, 98, 0.1);
        }
        
        .known-ticket-item__thumb {
          width: 48px;
          height: 48px;
          border-radius: 8px;
          background: rgba(255, 255, 255, 0.08);
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          flex-shrink: 0;
        }
        
        .known-ticket-item__thumb img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        
        .known-ticket-item__thumb svg {
          color: #666;
        }
        
        .known-ticket-item__info {
          flex: 1;
          min-width: 0;
        }
        
        .known-ticket-item__seat {
          color: #FFF;
          font-size: 14px;
          font-weight: 600;
          margin-bottom: 4px;
        }
        
        .known-ticket-item__type {
          color: #888;
          font-size: 12px;
        }
        
        /* Fullscreen Mode Adjustments */
        .is-fullscreen .arena-mobile-nav {
          display: none;
        }
        
        .is-fullscreen .arena-page {
          padding-bottom: 0;
        }
        
        .is-fullscreen .arena-desktop-sidebar {
          display: none !important;
        }
        
        .is-fullscreen .ticket-results-panel {
          display: none;
        }
        
        /* Desktop Sheet positioning */
        @media (min-width: 900px) {
          .jump-sheet-overlay,
          .known-tickets-overlay {
            align-items: center;
          }
          
          .jump-sheet,
          .known-tickets-sheet {
            border-radius: 16px;
            max-height: 80vh;
          }
        }
      `}</style>
      </div>
    </FullscreenContext.Provider>
  );
};

export default TicketRegistryPage;
